/**
 * ⚠ FILE OWNERSHIP LOCK (owner, 2026-08-16) — READ BEFORE EDITING.
 *
 * This file is owned EXCLUSIVELY by the duel/AI session until Commit B is
 * finished. A prior collision between two concurrent sessions editing this
 * file corrupted HEAD (134d198 interleaved two drafts mid-write; repaired at
 * da30e1f, re-broken and re-yielded at ea13646). It cannot happen again.
 *
 * If you are the Mythic/Celestial removal session (or any other): DO NOT EDIT
 * THIS FILE now, including to strip Mythic/Titan Strike code back out. Queue
 * the removal — it will be a small, easy diff against clean HEAD once the
 * lock lifts — rather than editing here directly while Commit B is live.
 *
 * Remove this banner only when Commit B is complete and ownership is
 * explicitly released. See SESSION_LOG.md, docs/duel/.
 */

/**
 * Layer 2 — individual AI, 10Hz. A hand-rolled behaviour tree. (§10.1)
 *
 * The brief offers `behaviortree.js` or a hand-roll. Hand-rolled, for one
 * reason that is not "avoid a dependency": a generic BT library allocates. It
 * builds node objects, blackboards and status records, and it does it inside a
 * loop that runs 10 times a second for up to 22 players in a process that also
 * has to hold a 60Hz tick under SERVER_TICK_BUDGET_MS (§13.3). §15.7 wants zero
 * allocation in the loop, and a tree of closures composed once at module load
 * gives us the same shape for free. The tree below is a real selector/sequence
 * tree — it is just built at import time and then only ever walked.
 *
 * ── The tree (§10.1) ────────────────────────────────────────────────────────
 *
 *   Root → Selector
 *     ├─ RestartStaged? → Take it / hold the legal distance
 *     ├─ HasBall?       → Evaluate options → Pass / Shoot / Dribble / Hold
 *     ├─ TeamHasBall?   → Make run / Provide angle / Hold shape
 *     ├─ BallLoose?     → Should I contest? → Pursue intercept point
 *     └─ OppHasBall?    → Press / Mark / Cover / Hold line
 *
 * ── What this layer emits, and what it must never do ────────────────────────
 *
 * It writes a DECISION into the brain's per-player arrays: a seek target, an aim
 * yaw, charges, and flags. It never touches WorldState. Layer 1 turns the seek
 * target into moveX/moveZ next tick and every tick after, until this layer runs
 * again ~6 ticks later. That gap is not a compromise — it is why an AI commits
 * to a decision for 100ms like a person instead of re-deciding 60 times a second
 * like a machine.
 *
 * ── Imperfect information (§10.2) ───────────────────────────────────────────
 *
 * `canSee()` gates every option this layer evaluates. An AI cannot pass to a
 * teammate behind it and cannot react to a carrier it has not turned to look at.
 * This is where "the AI is cheating" is won or lost: not in the speed numbers,
 * which are shared with humans and therefore unimpeachable, but in whether the
 * AI acts on information a human at that spot facing that way could not have.
 */

import type { InputCommand, WorldState } from '../sim/types.js';
import { InputFlags, MAX_PLAYERS, MatchPhase, RestartStage } from '../sim/types.js';
import {
  AI_AWARENESS_RADIUS,
  AI_TACKLE_COMMIT_MARGIN,
  SIMULATION_TICK_RATE,
  MAX_SPEED_SPRINT,
  CONTAIN_DIST_MIN_M,
  CONTAIN_DIST_MAX_M,
  CONTAIN_SPEED_GIVE,
  CONTAIN_SHOOTING_RANGE_M,
  CONTAIN_SHOOTING_TIGHTEN,
  ENGAGE_EXIT,
  ENGAGE_ENTER,
  ENGAGE_GRACE_MAX_S,
  ENGAGE_PROGRESS_CLOSE_RATE,
  ENGAGE_PROGRESS_MARGIN_RATE,
  DUEL_MIN_APPROACH_S,
  DUEL_MIN_CONTAIN_S,
  DUEL_MIN_ENGAGE_S,
  DUEL_RECOVER_S,
  AI_STRIKE_BUFFER_TICKS,
  BALL_TRAVEL_MAX_HORIZON_S,
  BALL_TRAVEL_SUBSTEP_S,
  BALL_PLAY_REACH,
  BALL_RADIUS,
  HEADER_CONTACT_HEIGHT,
  HEADER_EXTRA_REACH,
  AI_HEADER_CLEARANCE_DIST,
  STRIKE_FACING_MIN_DIST,
  STRIKE_MAX_HEIGHT,
  STRIKE_MIN_FACING_DOT,
  AI_AIR_INTERCEPT_TRUST,
  AI_BALL_PREDICT_DAMP,
  AI_BOX_RUN_SEARCH_RADIUS,
  AI_BUILD_UP_FX,
  AI_CARRIER_SCAN_EXTRA_DEG,
  AI_CARRIER_SCAN_PRESSURE_MAX,
  AI_CLEARANCE_INFIELD_FRAC,
  AI_CLEARANCE_MIN_FORWARD,
  AI_DRIBBLE_LOOKAHEAD,
  AI_TOUCHLINE_GUARD_M,
  AI_SHIELD_MAX_BACKWARD,
  AI_BOOST_TIER2_RACE_S,
  AI_BOOST_SPACE_MIN,
  AI_BOOST2_STAMINA_FLOOR,
  AI_BOOST_STAMINA_FLOOR,
  AI_BOOST_DUEL_CLOSE_MIN_M,
  AI_BAIL_RELIEF,
  AI_BAIL_MAX_M,
  AI_BAIL_MIN_M,
  AI_TROUBLE_PRESSURE,
  AI_BOX_RUN_POST_Z,
  AI_CROSS_LIFT,
  AI_DRIBBLE_UNPRESSED_MAX,
  AI_FINAL_THIRD_FX,
  AI_INTERCEPT_ITERATIONS,
  AI_KICK_COOLDOWN_TICKS,
  AI_LOOSE_CLAIM_RADIUS,
  AI_MARK_GOALSIDE_DISTANCE,
  AI_OFFER_CONE_HALF_DEG,
  AI_OFFER_MAX_ANCHOR_DRIFT,
  AI_OFFER_SEARCH_RADIUS,
  AI_OPTION_MIN_SCORE,
  AI_OUT_RISK_MISS_FLOOR,
  AI_SCORE_CROSS_BASE,
  AI_SCORE_CROSS_TARGET_SPACE_WEIGHT,
  AI_SCORE_PASS_SWITCH_WEIGHT,
  AI_SWITCH_MIN_LATERAL,
  AI_TEAMMATE_AWARENESS_RADIUS,
  CROSS_WIDE_FRAC,
  AI_PASS_ARRIVAL_IDEAL,
  AI_PASS_ARRIVAL_MAX,
  AI_PASS_ASSIST_CHARGE_FACTOR,
  AI_PASS_LOFT_OVERRUN,
  AI_PASS_PITCH_MARGIN,
  AI_PASS_DECEL_ESTIMATE,
  AI_PASS_LANE_BLOCK_RADIUS,
  AI_PASS_LANE_SAFE_RADIUS,
  AI_PASS_LAYOFF_CONE_DEG,
  AI_PASS_LAYOFF_RADIUS,
  AI_PASS_MAX_COMFORTABLE_DISTANCE,
  AI_PASS_MIN_DISTANCE,
  AI_PASS_TARGET_ARRIVAL_SPEED,
  AI_SCORE_ARRIVAL_PENALTY,
  AI_SCORE_OUT_RISK_PENALTY,
  AI_SCORE_BACKWARD_PASS_PENALTY,
  AI_SCORE_DRIBBLE_BASE,
  AI_SCORE_DRIBBLE_FORWARD_BONUS,
  AI_SCORE_DRIBBLE_SPACE_WEIGHT,
  AI_SCORE_PASS_BASE,
  AI_SCORE_PASS_CALL_BONUS,
  AI_SCORE_PASS_CIRCULATE_BONUS,
  AI_SCORE_PASS_DISTANCE_PENALTY,
  AI_SCORE_PASS_FINAL_THIRD_BONUS,
  AI_SCORE_PASS_HUMAN_BONUS,
  AI_SCORE_PASS_LANE_SAFETY_WEIGHT,
  AI_SCORE_PASS_PROGRESS_BONUS,
  AI_SCORE_PASS_PROGRESS_WEIGHT,
  AI_SCORE_PASS_SPACE_WEIGHT,
  AI_SCORE_PRESSURE_PENALTY,
  AI_SCORE_SHOOT_BASE,
  AI_SCORE_SHOOT_BOX_BONUS,
  AI_SHOT_BOX_RANGE_BONUS,
  AI_SHOT_CHARGE_NEAR_DIST,
  AI_SHOT_CHARGE_NEAR_T2,
  AI_SHOT_LANE_BLOCK_RADIUS,
  AI_SHOT_LANE_FLOOR,
  AI_SHOT_LIFT_FAR,
  AI_SHOT_LIFT_NEAR,
  AI_SHOT_POST_FRAC2,
  AI_SHOT_RANGE_FALLOFF,
  AI_LUNGE_LAST_DEFENDER_MULT,
  AI_LUNGE_REQUIRED_MARGIN,
  AI_SLIDE_MIN_RANGE,
  AI_SPACE_CONTROL_SCALE,
  AI_SPRINT_URGENCY_DISTANCE,
  AI_STAMINA_SPRINT_FLOOR,
  AI_SPRINT_STAMINA_BAND,
  AI_SPRINT_HOLD_TICKS,
  AI_STUCK_PRESSURE,
  AI_SUPPORT_ATTACK_PUSH_FX,
  AI_SUPPORT_BOX_PUSH_FX,
  AI_SUPPORT_CELL_KEEP_BONUS,
  AI_SUPPORT_SEARCH_RADIUS,
  AI_TACKLE_COOLDOWN_TICKS,
  AI_THROUGH_BALL_MIN_DISTANCE,
  AI_VISION_CONE_DEG,
  GOAL_WIDTH,
  GROUND_PASS_MAX_SPEED,
  GROUND_PASS_MIN_SPEED,
  KICKOFF_OPPONENT_DISTANCE,
  MAX_SPEED_RUN,
  PASS_ASSIST_SPEED_BASE,
  PASS_ASSIST_SPEED_PER_M,
  PASS_LEAD_CONTROL_FOOT_STEP,
  PASS_LEAD_ITERS,
  THROUGH_BALL_LEAD_CAP_S,
  PASS_CHARGE_MAX_S,
  PASS_CHARGE_MIN_S,
  PENALTY_AREA_LENGTH,
  PENALTY_AREA_WIDTH,
  POSSESSION_CAPTURE_REL_SPEED,
  POSSESSION_RADIUS,
  PRESSURE_RADIUS,
  SHOT_CHARGE_MAX_S,
  SHOT_CHARGE_MIN_S,
  SLIDE_TACKLE_RANGE,
  STANDING_TACKLE_RANGE,
  STEAL_AWARENESS_RANGE,
  STEAL_CHALLENGE_RANGE,
  STEAL_ENGAGEMENT_RANGE,
} from '../sim/constants.js';
// THE ball-first predicate, shared verbatim with resolveTackle. The AI must
// commit only to challenges the sim will judge legal, and the only way to
// guarantee that is to ask the sim's own function. (§4.4 — see the file header
// for what two copies of it cost.)
import { ballFirstMargin, reachesBallBeforeMan } from '../sim/tackle-geometry.js';
import { ballExposure, engageStepPoint, scoreEngageOpportunity } from '../sim/duel.js';
import type { DifficultyParams } from './difficulty.js';
import {
  createBallPredict,
  createBallTravel,
  createIntercept,
  groundDecel,
  groundRunOut,
  predictBall,
  solveIntercept,
  travelForDistance,
} from './ball-travel.js';
import { isForwardPosition } from './formations.js';
import type { InfluenceMap, SpacePoint } from './influence.js';
import { createSpacePoint } from './influence.js';
import type { Pcg32 } from '../math/pcg32.js';
import { pcg32Float, pcg32GaussianClamped } from '../math/pcg32.js';
import { DEG2RAD, PI, angleDelta, clamp, clamp01, datan2, dcos, dsin, remapClamped } from '../math/vec3.js';

// ---------------------------------------------------------------------------
// The Layer 3 → Layer 2 contract
// ---------------------------------------------------------------------------

/**
 * The team's tactical phase. (§10.1 Layer 3)
 *
 * Owned by Layer 3, declared here with the rest of the L3→L2 interface so the
 * dependency runs one way (team-ai → individual → steering/influence). A cycle
 * would work — ESM tolerates it and nothing here is read at module-init time —
 * but "works if you don't look at it" is not a property worth having in the
 * module graph.
 */
export const enum TeamPhase {
  BuildUp = 0,
  Attack = 1,
  FinalThird = 2,
  Transition = 3,
  Press = 4,
  DefendBlock = 5,
}

/** What Layer 3 has told this player his job is for the next ~200ms. */
export const enum Assignment {
  HoldShape = 0,
  Support = 1,
  Carry = 2,
  Chase = 3,
  Press = 4,
  Mark = 5,
  Cover = 6,
  /**
   * SHOW FOR THE BALL. In our possession only: rotate around the carrier onto
   * the nearest edge of the sector he can see, at the radius you already had.
   * See offerPoint and the AI_OFFER_* block in constants.ts — the preserved
   * radius is what makes this an angle rather than a swarm.
   */
  Offer = 7,
}

/** The one-shot kick this player wants. Consumed by Layer 1, then cleared. */
export const enum KickIntent {
  None = 0,
  Pass = 1,
  ThroughBall = 2,
  Shoot = 3,
}

/** One-shot challenges. Same latch discipline as KickIntent. */
export const enum ActionIntent {
  None = 0,
  StandingTackle = 1,
  SlideTackle = 2,
}

/** Distinct RNG salts. Two draws sharing a salt on one tick return the SAME
 *  number — the streams are keyed by (tick, player, salt). Never reuse one. */
export const SALT_REACTION = 1;
export const SALT_AIM = 2;
export const SALT_SLIDE = 3;
export const SALT_LUNGE = 4;

/**
 * Everything Layer 2 is allowed to see and touch. Implemented by the Layer 3
 * TeamAI, which owns all the storage.
 */
export interface AiBrain {
  world: WorldState;
  team: number;
  /** +1 if this team attacks +X this half, −1 otherwise. */
  attackSign: number;
  params: DifficultyParams;
  phase: TeamPhase;
  influence: InfluenceMap;

  /** Attack-axis projection past which a player of this team is offside. */
  onsideLimitProj: number;

  // Layer 3 output, indexed by PlayerId.
  assignment: Uint8Array;
  markTarget: Int32Array;
  /** 1 ⇒ slot is human-controlled (server pre-filled its input this tick). The
   *  co-op pass bonus (§13) reads this; captured before the AI fills `out`. */
  humanMask: Uint8Array;
  /** 1 ⇒ this teammate is actively Calling-for-Pass (X) this decision — the
   *  carrier strongly prefers feeding them (§8). Set from the input flag and held
   *  for a short window by Layer 3 so the 10Hz decision loop can respond. */
  callActive: Uint8Array;
  /** §8 — the human has issued a team CIRCULATE (single middle-click). While true,
   *  a BOT carrier plays a safe pass to another BOT now (never holds/dribbles on),
   *  and the human is excluded as a target unless there is no other safe option. */
  circulateActive: boolean;
  shapeX: Float64Array;
  shapeZ: Float64Array;

  // Layer 2 output, indexed by PlayerId. Read by Layer 1 every tick.
  decSeekX: Float64Array;
  decSeekZ: Float64Array;
  decAimYaw: Float64Array;
  decFlags: Int32Array;
  decKick: Uint8Array;
  /** Tick the latched `decKick` was decided on. A strike waits (up to
   *  AI_STRIKE_BUFFER_TICKS) for the ball to actually be on the boot rather than
   *  being thrown away on the decision tick — see applyDecisionToInput. */
  decKickTick: Int32Array;
  decAction: Uint8Array;
  decCharge: Float64Array;
  decLift: Float64Array;
  /**
   * §3 - WHO THIS PASS IS FOR. The teammate `emitPass`/`emitCross` chose, or -1.
   *
   * This channel exists because the identity was being THROWN AWAY. decideOnBall
   * picks a man for a reason - he is the one in space with a clear lane - and
   * then emitted nothing but an aim yaw, so the moment the ball left the boot
   * Layer 3 saw only "a loose ball" and handed it to whoever had the soonest
   * intercept. MEASURED (chance-creation.test.ts, 4 seeds x 5 sim-min): the man
   * the ball was actually aimed at was the team's assigned receiver only 25.3 %
   * of the time; three passes in four were collected - or not collected - by a
   * player who had no idea it was coming, while the intended man held his shape.
   * A pass-based game cannot be built on that.
   *
   * Cleared with the rest of the latch on every fresh decision, so a stale
   * target can never be published.
   */
  decPassTarget: Int32Array;
  kickCooldown: Int32Array;
  tackleCooldown: Int32Array;

  /**
   * THE DUEL (Phase-1 Commit 7). Per-player `DuelState`, plus how long he has
   * been in it (ticks).
   *
   * ⚠ WHY IT IS PER-PLAYER AND NOT PER-ASSIGNMENT. The duel used to live
   * entirely inside the `Press` branch, which meant only the man Layer 3 had
   * NAMED as the presser could ever challenge. MEASURED on the fair-stealing
   * fixture: the defender closed to 0.25 m from the carrier and `everTackled`
   * was FALSE — he was holding shape, the challenge code never ran, and before
   * Commit 1 the ball simply transferred to him by proximity. Being next to
   * the man with the ball is a situation, not a job title.
   */
  duelState: Int32Array;
  duelTicks: Int32Array;
  /** Sprint gear latch + its minimum-hold countdown. A gear change is a
   *  decision and needs hysteresis; see setSprint. */
  sprintLatch: Uint8Array;
  sprintHold: Int32Array;
  /** 1 while this player is actually participating in a duel. Drives the
   *  single on-exit reset, and lets telemetry count ACTIVE duel time rather
   *  than time-since-the-enum-last-changed. */
  inDuel: Uint8Array;
  /**
   * ENGAGE progress memory (Commit B1): the previous ENGAGE decision's tick,
   * ball-first margin and defender→ball distance, so the exit can ask "is the
   * step still working" as a RATE over the decision interval, and the grace
   * ticks it has already been granted.
   */
  engPrevTick: Int32Array;
  engPrevMargin: Float64Array;
  engPrevBallDist: Float64Array;
  engGraceTicks: Int32Array;
  /** Last tick's ball exposure, for the trend term — a rising exposure is a
   *  bad touch happening now, which is when a defender steps. */
  prevExposure: Float64Array;

  /**
   * §3 - the LIVE pass this team has in flight: who it is FOR, who PLAYED it,
   * and the tick the verb actually went out.
   *
   * Written by applyDecisionToInput on the tick the strike is really emitted (not
   * the tick it was decided - the strike buffer can hold an intent for up to
   * AI_STRIKE_BUFFER_TICKS), and read by Layer 3's applyPassIntent.
   *
   * WARNING: the liveness test is `ball.lastTouchedBy === passIntentFrom`, and it
   * must be. The obvious version - stamp `ball.lastTouchTick` here and require it
   * not to change - is WRONG and silently disables the whole feature: the AI runs
   * BEFORE step(), step() increments the tick FIRST (step.ts:62) and only then
   * resolves the kick, so the strike is stamped at tick+1 and the recorded
   * "unchanged" value never matches again. MEASURED as an exactly-zero-effect
   * change: 2549 carrier decisions, 246 passes, 146 completions - byte-identical
   * to the run without it.
   */
  passIntentTarget: number;
  passIntentFrom: number;
  passIntentTick: number;

  /** A scratch generator seeded from (matchSeed, tick, id, salt). Stateless. */
  rngFor(id: number, salt: number): Pcg32;
}

// ---------------------------------------------------------------------------
// Scratch — allocated once (§15.7)
// ---------------------------------------------------------------------------

const candId = new Int32Array(MAX_PLAYERS);
const candDist = new Float64Array(MAX_PLAYERS);
const space: SpacePoint = createSpacePoint();
/** Scratch for the ENGAGE step target (no steady-state allocation). */
const stepPt = { x: 0, z: 0 };
const interceptScratch = createIntercept();
const oppScratch = createIntercept();
const travelScratch = createBallTravel();
const trapScratch = createBallPredict();

// ---------------------------------------------------------------------------
// On-ball decision telemetry (§8) — OFF by default, zero cost when null.
// A measurement affordance: set a sink and every on-ball decision records the
// option scores that produced it, so "the carrier held with an open man" is a
// number instead of an anecdote. Never allocated or written in normal play.
// ---------------------------------------------------------------------------

export interface OnBallDecisionRecord {
  tick: number;
  id: number;
  team: number;
  /** 0 hold/shield, 1 shoot, 2 pass, 3 dribble. */
  kind: number;
  score: number;
  pressure: number;
  /** Visible teammates ≥ min distance considered as pass candidates. */
  passSeen: number;
  /** Of those, how many were dropped because their lane was blocked. */
  passLaneBlocked: number;
  /** Of those, how many were usable (clear lane) AND advanced play. */
  usableForward: number;
  /** Best pass score found (−inf if none), and its progress (−1..1). */
  bestPassScore: number;
  bestPassProgress: number;
  /**
   * GATE-BY-GATE, why a teammate never became an option. Filled by
   * collectPassOptions; the four rejects plus `passSeen` account for every
   * outfield teammate in `mates`. Measurement only (the sink is null in play).
   * MEASURED with these: only 2.27 of ~9 teammates reach the scorer at all,
   * which is a far bigger loss than the 0.98 that are lane-blocked - so "the
   * carrier has no option" is a VISIBILITY problem before it is a lane problem,
   * and 69.7 % of every teammate a carrier had was rejected for one single
   * reason: OUTSIDE THE FACING CONE.
   */
  mates: number;
  rejTooClose: number;
  rejTooFar: number;
  rejCone: number;
  rejOffside: number;
  /** How many candidates were inside AI_PASS_LAYOFF_RADIUS — i.e. exempt from
   *  the facing cone because a near teammate is not an information problem. */
  layoffExempt: number;
  /** Scan cone actually used this decision, degrees (140 -> 220 unpressed). */
  scanDeg: number;
}

/** Gate tallies for the telemetry record above - written by collectPassOptions,
 *  read by decideOnBall on the same call. Module scratch, never allocated. */
let gateMates = 0;
let gateTooClose = 0;
let gateTooFar = 0;
let gateCone = 0;
let gateOffside = 0;
let gateLayoff = 0;
let gateScanDeg = 0;

let onBallDebug: OnBallDecisionRecord[] | null = null;
export function setOnBallDebug(sink: OnBallDecisionRecord[] | null): void {
  onBallDebug = sink;
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function distXZ(ax: number, az: number, bx: number, bz: number): number {
  const dx = ax - bx;
  const dz = az - bz;
  return Math.sqrt(dx * dx + dz * dz);
}

/** Distance from a point to a segment in the XZ plane. Pass lanes are segments. */
function pointSegmentDistXZ(
  px: number, pz: number,
  ax: number, az: number,
  bx: number, bz: number,
): number {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz;
  if (lenSq === 0) return distXZ(px, pz, ax, az);
  const t = clamp01(((px - ax) * dx + (pz - az) * dz) / lenSq);
  return distXZ(px, pz, ax + dx * t, az + dz * t);
}

/**
 * Can `id` see the point (tx, tz)? Awareness radius + vision cone. (§10.2)
 *
 * "It shouldn't react to a player behind it" is the brief's whole ask, and this
 * is the only thing standing between us and an AI that does. Everything Layer 2
 * evaluates goes through here first.
 */
export function canSee(state: WorldState, id: number, tx: number, tz: number): boolean {
  const p = state.players;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  const d = distXZ(px, pz, tx, tz);
  if (d > AI_AWARENESS_RADIUS) return false;
  return canSeeDirection(state, id, tx, tz);
}

/** The facing-cone half of canSee, with no distance gate — used for TEAMMATE
 *  pass candidates, whose distance gate is AI_TEAMMATE_AWARENESS_RADIUS (you
 *  know your own shape; you still cannot pass to a man behind your head). */
function canSeeDirection(state: WorldState, id: number, tx: number, tz: number): boolean {
  return withinCone(state, id, tx, tz, AI_VISION_CONE_DEG * 0.5 * DEG2RAD);
}

/** Is (tx,tz) inside a cone of half-angle `halfRad` about `id`'s facing? The
 *  facing test both canSeeDirection and the carrier's wider SCAN cone share, so
 *  there is exactly one piece of angle arithmetic to be wrong. */
function withinCone(state: WorldState, id: number, tx: number, tz: number, halfRad: number): boolean {
  const p = state.players;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  // Standing on top of it: you know it's there whichever way you face.
  if (distXZ(px, pz, tx, tz) < 1e-3) return true;
  if (halfRad >= PI) return true;
  const to = datan2(tz - pz, tx - px);
  return Math.abs(angleDelta(p.yaw[id]!, to)) <= halfRad;
}

/** Smallest distance any opponent of `team` sits from the segment a→b. */
function laneClearance(
  state: WorldState,
  team: number,
  ax: number, az: number,
  bx: number, bz: number,
  excludeA: number,
  excludeB: number,
): number {
  const p = state.players;
  let min = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! === team) continue;
    if (i === excludeA || i === excludeB) continue;
    const d = pointSegmentDistXZ(p.px[i]!, p.pz[i]!, ax, az, bx, bz);
    if (d < min) min = d;
  }
  return min;
}

/** Nearest opponent distance — the same pressure notion ball.ts uses (§4.2). */
function pressureOn(state: WorldState, id: number): number {
  const p = state.players;
  const team = p.team[id]!;
  let nearest = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === team) continue;
    const d = distXZ(p.px[id]!, p.pz[id]!, p.px[i]!, p.pz[i]!);
    if (d < nearest) nearest = d;
  }
  if (nearest >= PRESSURE_RADIUS) return 0;
  return 1 - nearest / PRESSURE_RADIUS;
}

/**
 * Would a Pass/Shoot flag emitted for `id` THIS tick actually strike the ball?
 *
 * A carbon copy of ball.ts findStriker's own envelope, for the same reason
 * ballReachableBeforeMan mirrors resolveTackle's: the AI must not spend a
 * decision on a strike the sim is going to ignore. findStriker requires the ball
 * inside BALL_PLAY_REACH (0.7 m) horizontally — possession reaches to 1.2 m, so
 * "he has the ball" is emphatically NOT "he can kick it" — and below his own
 * reach ceiling. A dribbling carrier is outside that window for most of every
 * knock cycle (MEASURED: 53 % of a carrier's ticks have the ball on the boot),
 * which is what made 90 % of AI kick intents evaporate.
 *
 * Deliberately ONE tick stale: the AI runs before step(), so the player moves
 * (stage 4) between this test and findStriker (stage 6). He is moving toward his
 * own ball, so this errs conservative — it can hold a strike one tick that would
 * have landed, never the reverse, and the buffer retries next tick anyway.
 *
 * A staged restart is NOT second-guessed here: the FSM owns that geometry (a
 * throw-in's THROW_TAKE_REACH is deliberately wider than a boot's) and it has its
 * own auto-take fallback, so from Ready onward the taker simply emits. Nobody
 * else may play a dead ball at all, and during Setup nothing can — gateInputs
 * strips the verbs and step.ts does not even call resolveKicks.
 */
function strikeWouldLand(b: AiBrain, id: number): boolean {
  const w = b.world;
  const p = w.players;
  const ball = w.ball;
  if (w.match.phase !== MatchPhase.InPlay) {
    return w.match.restartStage !== RestartStage.Setup && w.match.restartTaker === id;
  }
  if (p.gkHoldStartTick[id]! >= 0) return false; // it is already in his own gloves
  const dx = ball.px - p.px[id]!;
  const dz = ball.pz - p.pz[id]!;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d > BALL_PLAY_REACH) return false;
  if (ball.py > p.py[id]! + STRIKE_MAX_HEIGHT + HEADER_EXTRA_REACH) return false;
  // The FACING half of findStriker's envelope (ball.ts facingAllowsStrike): you
  // cannot strike a ball behind your shoulders. Mirrored here for the reason this
  // whole function exists — an intent the sim will refuse must not be emitted,
  // because applyDecisionToInput charges AI_KICK_COOLDOWN_TICKS on the tick the
  // verb goes out, so an impossible strike would buy 18 ticks of silence. The
  // strike BUFFER handles the wait: the intent is held and fires on the first tick
  // the body has actually come round to the ball. Guarded against the degenerate
  // ball-on-the-axis divide exactly as the sim guards it (a NaN here reaches the
  // world hash — ai/no-nan.test.ts).
  if (d < STRIKE_FACING_MIN_DIST) return true;
  return (dcos(p.yaw[id]!) * dx + dsin(p.yaw[id]!) * dz) / d >= STRIKE_MIN_FACING_DOT;
}

/** Normalize an influence-map control value into roughly [-1, 1] for scoring. */
function normControl(c: number): number {
  return clamp(c * AI_SPACE_CONTROL_SCALE, -1, 1);
}

function halfLengthOf(b: AiBrain): number {
  return b.world.config.pitchLength * 0.5;
}

/**
 * Is player `id` inside the opponent's penalty area? A chance in here is always
 * worth a shot (§4.3) and a supporting forward wants to be here for a cutback.
 */
function inAttackingBox(b: AiBrain, id: number): boolean {
  const p = b.world.players;
  const intoAttackHalf = b.attackSign * p.px[id]!;
  if (intoAttackHalf < halfLengthOf(b) - PENALTY_AREA_LENGTH) return false;
  return Math.abs(p.pz[id]!) <= PENALTY_AREA_WIDTH * 0.5;
}

// ---------------------------------------------------------------------------
// Decision writers — the only way this layer speaks to Layer 1
// ---------------------------------------------------------------------------

function seek(b: AiBrain, id: number, x: number, z: number): void {
  b.decSeekX[id] = x;
  b.decSeekZ[id] = z;
}

/** Face where you are going, unless a decision has already set an aim. */
function faceSeek(b: AiBrain, id: number): void {
  const p = b.world.players;
  const dx = b.decSeekX[id]! - p.px[id]!;
  const dz = b.decSeekZ[id]! - p.pz[id]!;
  if (dx * dx + dz * dz < 1e-6) {
    b.decAimYaw[id] = p.yaw[id]!;
    return;
  }
  b.decAimYaw[id] = datan2(dz, dx);
}

/**
 * Sprint gating. Note what is NOT here: any tier check. Difficulty may not buy
 * a sprint (§10.2, §10.3) — this is the same stamina model, the same speed cap,
 * and the same Shift key a human presses.
 */
/**
 * ⚠ A GEAR CHANGE IS A DECISION, AND DECISIONS NEED HYSTERESIS.
 *
 * This was two bare thresholds — `want` (a distance test at the call site) and
 * a stamina floor — and both of them limit-cycle. Sprinting burns stamina, so
 * a bot hovering at AI_STAMINA_SPRINT_FLOOR toggles sprint on, drops below the
 * floor, toggles off, regenerates, and repeats. A bot closing on a target
 * crosses AI_SPRINT_URGENCY_DISTANCE the same way.
 *
 * MEASURED, and it is not a cosmetic problem: on the 5-minute AI telemetry the
 * Run<->Jog animation pair alone was 2,882 changes — 31 % of ALL animation
 * transitions in the match, with Jog<->Walk and Run<->Sprint taking the total
 * to 54 %. The flip speeds cluster at p50 = 3.15 m/s, which is EXACTLY
 * MAX_SPEED_JOG (3.5) minus ANIM_BAND_HYSTERESIS (0.35): the animation
 * hysteresis was doing its job perfectly and honestly reporting a player whose
 * SPEED was oscillating 3.15 <-> 3.50 several times a second.
 *
 * Two guards, because there are two thresholds:
 *   • stamina gets a band — start sprinting above floor + margin, keep going
 *     until below floor - margin, so the drain/regen cycle cannot ring.
 *   • the decision gets a minimum hold, so a wobbling distance test cannot
 *     change gear faster than a real player could.
 */
function setSprint(b: AiBrain, id: number, want: boolean): void {
  const p = b.world.players;
  const wasSprinting = b.sprintLatch[id] === 1;
  const stam = p.stamina[id]!;
  const staminaOk = wasSprinting
    ? stam > AI_STAMINA_SPRINT_FLOOR - AI_SPRINT_STAMINA_BAND
    : stam > AI_STAMINA_SPRINT_FLOOR + AI_SPRINT_STAMINA_BAND;
  let next = want && staminaOk;
  // Minimum time in gear. Stamina exhaustion is an override — a bot with an
  // empty bar must be allowed to stop, hold or no hold.
  if (next !== wasSprinting && b.sprintHold[id]! > 0 && staminaOk) {
    next = wasSprinting;
  }
  if (next !== wasSprinting) b.sprintHold[id] = AI_SPRINT_HOLD_TICKS;
  b.sprintLatch[id] = next ? 1 : 0;
  if (next) b.decFlags[id] = b.decFlags[id]! | InputFlags.Sprint;
}

/**
 * BOOST — the bots may spend it too (owner, 2026-08-16: "let the bots use the
 * boost").
 *
 * `setSprint` was the AI's ONLY gear: `InputFlags.Boost1/Boost2` appear
 * nowhere in this file, so the whole two-tier boost the player has was
 * human-only, and a bot could never win a race it was losing. It stays a
 * SPENDING decision, not a default: it costs stamina at 2x (tier 1) or 4x
 * (tier 2) the run rate, so it is granted only when the moment is worth it
 * AND the bar can afford it, and never below AI_BOOST_STAMINA_FLOOR - a bot
 * that empties its bar chasing a lost cause is a bot that walks for the next
 * thirty seconds.
 */
function setBoost(b: AiBrain, id: number, tier: 1 | 2): void {
  const p = b.world.players;
  if (p.stamina[id]! <= AI_BOOST_STAMINA_FLOOR) return;
  b.decFlags[id] = b.decFlags[id]! | InputFlags.Sprint | InputFlags.Boost1;
  if (tier >= 2 && p.stamina[id]! > AI_BOOST2_STAMINA_FLOOR) {
    b.decFlags[id] = b.decFlags[id]! | InputFlags.Boost2;
  }
}

function sprintIfFar(b: AiBrain, id: number): void {
  const p = b.world.players;
  const d = distXZ(p.px[id]!, p.pz[id]!, b.decSeekX[id]!, b.decSeekZ[id]!);
  setSprint(b, id, d > AI_SPRINT_URGENCY_DISTANCE);
  // NO BOOST HERE, deliberately. sprintIfFar is the generic "target is far"
  // helper behind Mark, Cover and hold-the-line as well as the support runs,
  // so a boost on this line is a boost on EVERY off-ball run. MEASURED: it
  // broke situational-shape's R5 - a losing side late sat at mean x -20.6
  // against -11.2 when winning, i.e. chasing the game made them DEEPER,
  // because the fastest thing on the pitch became the recovery run. Boost is
  // a decision about one moment, and it lives at the moments below.
}

/** The aim jitter §10.3 scales. On top of the sim's own error model (§4.2). */
function aimWithError(b: AiBrain, id: number, yaw: number): number {
  const e = pcg32GaussianClamped(b.rngFor(id, SALT_AIM)) * b.params.aimErrorRad;
  return yaw + e;
}

// ---------------------------------------------------------------------------
// A tiny behaviour tree
// ---------------------------------------------------------------------------

const enum Bt {
  Failure = 0,
  Success = 1,
}

type BtNode = (b: AiBrain, id: number) => Bt;

/** First child that succeeds wins. */
function selector(children: readonly BtNode[]): BtNode {
  return (b, id) => {
    for (let i = 0; i < children.length; i++) {
      if (children[i]!(b, id) === Bt.Success) return Bt.Success;
    }
    return Bt.Failure;
  };
}

/** All children must succeed, in order. */
function sequence(children: readonly BtNode[]): BtNode {
  return (b, id) => {
    for (let i = 0; i < children.length; i++) {
      if (children[i]!(b, id) === Bt.Failure) return Bt.Failure;
    }
    return Bt.Success;
  };
}

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

const restartStaged: BtNode = (b) => {
  const phase = b.world.match.phase;
  return phase !== MatchPhase.InPlay && phase !== MatchPhase.Warmup ? Bt.Success : Bt.Failure;
};

const hasBall: BtNode = (b, id) => (b.world.players.possessing[id] === 1 ? Bt.Success : Bt.Failure);

const teamHasBall: BtNode = (b, id) => {
  const owner = b.world.ball.possessedBy;
  if (owner < 0) return Bt.Failure;
  return b.world.players.team[owner]! === b.world.players.team[id]! ? Bt.Success : Bt.Failure;
};

const ballLoose: BtNode = (b) => (b.world.ball.possessedBy < 0 ? Bt.Success : Bt.Failure);

const oppHasBall: BtNode = (b, id) => {
  const owner = b.world.ball.possessedBy;
  if (owner < 0) return Bt.Failure;
  return b.world.players.team[owner]! !== b.world.players.team[id]! ? Bt.Success : Bt.Failure;
};

// ---------------------------------------------------------------------------
// Restart behaviour (§5.1)
// ---------------------------------------------------------------------------

/**
 * Take the restart, or clear out and let it be taken.
 *
 * The defending team's 9.15m is enforced HERE and nowhere else. rules/fsm.ts
 * stages restarts and gates who may play the ball, but nothing in the sim pushes
 * defenders off the spot (FREE_KICK_WALL_DISTANCE and KICKOFF_OPPONENT_DISTANCE
 * exist as constants with no enforcement). A human can stand on the ball at a
 * kickoff; our AI chooses not to, because eleven AI players swarming a dead ball
 * is unwatchable. This is voluntary law-abiding, not a rules implementation —
 * see the report.
 */
const decideRestart: BtNode = (b, id) => {
  const m = b.world.match;
  const p = b.world.players;
  const onRestartTeam = p.team[id]! === m.restartTeam;

  if (onRestartTeam && m.restartTaker === id) {
    const bx = b.world.ball.px;
    const bz = b.world.ball.pz;
    seek(b, id, bx, bz);
    faceSeek(b, id);
    const d = distXZ(p.px[id]!, p.pz[id]!, bx, bz);
    // In reach and allowed to play it: this is a normal on-ball decision, so
    // reuse the option evaluator rather than inventing a second one. Restarts
    // taken by AI then look like passes, because they are passes.
    if (d <= POSSESSION_RADIUS) {
      decideOnBall(b, id);
      if (b.decKick[id] === KickIntent.None && b.kickCooldown[id]! <= 0) {
        // Nothing scored well enough, but a restart must actually be restarted.
        // Play the safest ball we can find rather than let the ready window lapse.
        forcePassAnywhere(b, id);
      }
    }
    return Bt.Success;
  }

  seek(b, id, b.shapeX[id]!, b.shapeZ[id]!);

  if (!onRestartTeam) {
    // Law 8/13: retreat to the legal distance from the spot.
    const dx = b.shapeX[id]! - m.restartX;
    const dz = b.shapeZ[id]! - m.restartZ;
    const d = Math.sqrt(dx * dx + dz * dz);
    if (d < KICKOFF_OPPONENT_DISTANCE) {
      // Push the anchor radially out to the legal ring. A degenerate zero vector
      // (anchor exactly on the spot) would divide by zero — back away toward our
      // own goal instead, which is where a defender would go anyway.
      if (d < 1e-3) {
        seek(b, id, m.restartX - b.attackSign * KICKOFF_OPPONENT_DISTANCE, m.restartZ);
      } else {
        const s = KICKOFF_OPPONENT_DISTANCE / d;
        seek(b, id, m.restartX + dx * s, m.restartZ + dz * s);
      }
    }
  }

  faceSeek(b, id);
  return Bt.Success;
};

/**
 * The yaw for a blind ball upfield — a clearance, or a restart with nobody to
 * aim at. Up the pitch, and INFIELD in proportion to how close to the touchline
 * he is.
 *
 * The old version aimed at exactly `attackSign > 0 ? 0 : PI` — straight up the X
 * axis — and then had aimWithError rotate it, and then handed the result to
 * buildPass, which re-scans a 60° cone for a receiver and re-aims at him. From
 * anywhere near a touchline that is a coin-flip on a throw-in, and MEASURED it
 * was: clearances and blind balls were 5.6 % of every ball put out of play, on
 * top of the passes.
 *
 * Aiming infield is not a hack, it is what a defender does: you clear it into
 * the middle where your own midfield is, not along the paint. The target point
 * is also clamped inside the pitch margin, so the yaw itself can never point at
 * a spot off the field.
 */
function clearanceAimYaw(b: AiBrain, id: number): number {
  const p = b.world.players;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  const halfL = halfLengthOf(b) - AI_PASS_PITCH_MARGIN;
  const halfW = b.world.config.pitchWidth * 0.5 - AI_PASS_PITCH_MARGIN;
  const tx = clamp(px + b.attackSign * AI_PASS_MAX_COMFORTABLE_DISTANCE, -halfL, halfL);
  const tz = clamp(pz * AI_CLEARANCE_INFIELD_FRAC, -halfW, halfW);
  const dx = tx - px;
  const dz = tz - pz;
  if (dx * dx + dz * dz < 1e-6) return b.attackSign > 0 ? 0 : PI;
  return datan2(dz, dx);
}

/** Last-resort restart: any visible teammate, else just play it upfield. */
function forcePassAnywhere(b: AiBrain, id: number): void {
  const p = b.world.players;
  const team = p.team[id]!;
  let best = -1;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === id || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    const d = distXZ(p.px[id]!, p.pz[id]!, p.px[i]!, p.pz[i]!);
    if (d < AI_PASS_MIN_DISTANCE) continue;
    // Prefer a teammate in space and not miles away.
    const s = normControl(b.influence.controlAt(p.px[i]!, p.pz[i]!)) - d / halfLengthOf(b);
    if (s > bestScore) {
      bestScore = s;
      best = i;
    }
  }

  if (best < 0) {
    b.decAimYaw[id] = aimWithError(b, id, clearanceAimYaw(b, id));
    b.decKick[id] = KickIntent.Pass;
    b.decCharge[id] = chargeForPassDistance(AI_PASS_MIN_DISTANCE * 3);
    return;
  }
  emitPass(b, id, best, false);
}

// ---------------------------------------------------------------------------
// On the ball: evaluate options → Pass / Shoot / Dribble / Hold (§10.1)
// ---------------------------------------------------------------------------

/**
 * The launch speed that gets the ball to `dist` still slow enough to trap.
 *
 * v₀ = √(v_arrive² + 2·a·d), the constant-deceleration solution. See
 * AI_PASS_TARGET_ARRIVAL_SPEED in constants.ts for why this is the number the
 * whole AI hinges on — weighting a pass is the difference between football and
 * 22 players chasing a ball nobody can stop.
 */
export function passSpeedForDistance(dist: number): number {
  const v = Math.sqrt(
    AI_PASS_TARGET_ARRIVAL_SPEED * AI_PASS_TARGET_ARRIVAL_SPEED + 2 * AI_PASS_DECEL_ESTIMATE * dist,
  );
  return clamp(v, GROUND_PASS_MIN_SPEED, GROUND_PASS_MAX_SPEED);
}

/**
 * Invert ball.ts's pass model to find the charge a human's thumb would hold.
 *
 * buildPass() maps charge → t → speed by lerping GROUND_PASS_MIN..MAX_SPEED, so
 * this is that lerp run backwards from the target speed above. Doing it this way
 * rather than picking a charge by feel means the AI and a human reach the same
 * ball speed through the same code — if the pass model is ever retuned, the AI
 * retunes with it and there is no second model to forget about.
 *
 * Only valid for ground/lofted passes. A driven pass (Sprint+Pass) uses a
 * different speed band, which is exactly why emitPass() drops the Sprint bit.
 */
export function chargeForPassDistance(dist: number): number {
  const speed = passSpeedForDistance(dist);
  const t = (speed - GROUND_PASS_MIN_SPEED) / (GROUND_PASS_MAX_SPEED - GROUND_PASS_MIN_SPEED);
  return PASS_CHARGE_MIN_S + t * (PASS_CHARGE_MAX_S - PASS_CHARGE_MIN_S);
}

/**
 * The speed the SIM will actually launch this pass at — a MIRROR of buildPass.
 *
 * This matters more than it looks. `chargeForPassDistance` above solves a charge
 * from AI_PASS_TARGET_ARRIVAL_SPEED, but buildPass (ball.ts) then OVERRIDES the
 * resulting speed whenever choosePassTarget finds a receiver: it refits
 * `PASS_ASSIST_SPEED_BASE + PASS_ASSIST_SPEED_PER_M·d` and lets the charge swing
 * it only ±15 % (PASS_ASSIST_CHARGE_SPAN). So the AI's own solve is NOT what
 * leaves the boot, and any AI reasoning about arrival that used it was reasoning
 * about a pass that does not exist.
 *
 * Mirroring it here is the same discipline `ballReachableBeforeMan` uses for
 * resolveTackle: the AI predicts what the sim will do by reading the sim's own
 * numbers, so a retune moves both together.
 */
function simPassLaunchSpeed(dist: number): number {
  const fit = PASS_ASSIST_SPEED_BASE + PASS_ASSIST_SPEED_PER_M * dist;
  return clamp(fit * AI_PASS_ASSIST_CHARGE_FACTOR, GROUND_PASS_MIN_SPEED, GROUND_PASS_MAX_SPEED);
}

/** Where the sim will actually aim the pass — a mirror of passControlFootPoint
 *  (ball.ts §4): a converged velocity intercept plus one step into the runner's
 *  stride, capped at THROUGH_BALL_LEAD_CAP_S of his own velocity. The AI has to
 *  know this point because it, not the man, is where the ball is going. */
const leadPoint = { x: 0, z: 0 };
function passLeadPoint(b: AiBrain, id: number, target: number, passSpeed: number): void {
  const p = b.world.players;
  const sx = p.px[id]!;
  const sz = p.pz[id]!;
  const tvx = p.vx[target]!;
  const tvz = p.vz[target]!;
  let leadT = 0;
  for (let it = 0; it < PASS_LEAD_ITERS; it++) {
    const fx = p.px[target]! + tvx * leadT;
    const fz = p.pz[target]! + tvz * leadT;
    leadT = clamp(distXZ(sx, sz, fx, fz) / passSpeed, 0, THROUGH_BALL_LEAD_CAP_S);
  }
  const tvsp = Math.sqrt(tvx * tvx + tvz * tvz);
  const step = tvsp > 0.5 ? PASS_LEAD_CONTROL_FOOT_STEP : 0;
  leadPoint.x = p.px[target]! + tvx * leadT + (tvsp > 0.5 ? (tvx / tvsp) * step : 0);
  leadPoint.z = p.pz[target]! + tvz * leadT + (tvsp > 0.5 ? (tvz / tvsp) * step : 0);
}

/**
 * How far a ray from (px,pz) along the unit vector (dx,dz) travels before it
 * leaves the pitch, inset by AI_PASS_PITCH_MARGIN. Slab method; a component of
 * exactly zero simply never bounds that axis.
 *
 * The goal line counts as an exit. A ball crossing it between the posts is a
 * goal rather than a throw-in, but a PASS that ends up there is a goal kick
 * either way, so treating both as "off the pitch" is right for this test.
 */
function pitchExitDistance(b: AiBrain, px: number, pz: number, dx: number, dz: number): number {
  const halfL = b.world.config.pitchLength * 0.5 - AI_PASS_PITCH_MARGIN;
  const halfW = b.world.config.pitchWidth * 0.5 - AI_PASS_PITCH_MARGIN;
  let tMax = Number.POSITIVE_INFINITY;
  if (dx > 1e-9) tMax = Math.min(tMax, (halfL - px) / dx);
  else if (dx < -1e-9) tMax = Math.min(tMax, (-halfL - px) / dx);
  if (dz > 1e-9) tMax = Math.min(tMax, (halfW - pz) / dz);
  else if (dz < -1e-9) tMax = Math.min(tMax, (-halfW - pz) / dz);
  return tMax > 0 ? tMax : 0;
}

/**
 * Will this pass be PLAYABLE when it arrives, and will the ball still be on the
 * pitch if it is not? Returns a score delta (≤ 0) — the two things the option
 * scorer was blind to, and between them the mechanism behind both of the owner's
 * complaints.
 *
 * ── 1. ARRIVAL SPEED ────────────────────────────────────────────────────────
 * updatePossession (ball.ts) refuses to hand a receiver the ball unless the
 * ball-vs-player closing speed is under POSSESSION_CAPTURE_REL_SPEED (5 m/s),
 * and tryIntercept reaches only ~0.46 m off his body. MEASURED before this
 * change (3 seeds × 3 sim-min): AI passes ARRIVED at a median of 12.3 m/s, and
 * the ball was loose for 43-63 % of all in-play time. The scorer had a distance
 * penalty, which is a proxy; this is the actual quantity.
 *
 * ── 2. WHERE IT STOPS ───────────────────────────────────────────────────────
 * MEASURED: 61 % of balls put out of play were a pass, through-ball or clearance
 * the AI chose to play. Nothing in the decision had ever asked where the ball
 * would end up, so a firm ball down the line to a winger — struck at a speed
 * that runs 30 m — was scored purely on progress. `groundRunOut` gives the
 * resting point, `pitchExitDistance` gives the line, and the difference is the
 * risk. A LOFTED ball gets AI_PASS_LOFT_OVERRUN of extra allowance because the
 * travel model does not model its bounce.
 *
 * Both terms are GRADED, not gates. A hard reject would let a carrier with only
 * bad options hold the ball forever, which is the dwell f4d9fb3 fixed; a penalty
 * lets an unplayable pass lose to a square one — and lose to dribbling — while
 * still beating nothing at all.
 */
function passExecutionPenalty(b: AiBrain, id: number, target: number, lofted: boolean): number {
  const p = b.world.players;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  const d0 = distXZ(px, pz, p.px[target]!, p.pz[target]!);
  const launch = simPassLaunchSpeed(d0);
  // The ball flies at the LEAD point, not the man (resolveAim, §4.2).
  passLeadPoint(b, id, target, launch);
  const dx = leadPoint.x - px;
  const dz = leadPoint.z - pz;
  const dist = Math.sqrt(dx * dx + dz * dz);
  if (dist < 1e-6) return 0;

  let penalty = 0;

  // 1. Arrival speed AT THE MAN — the distance the sim's own power fit is
  //    calibrated for. MEASURED: for 4 m ≤ d ≤ 30 m this comes out at 1-3 m/s,
  //    comfortably inside POSSESSION_CAPTURE_REL_SPEED, so this term is normally
  //    zero and only bites where the ground-pass band CLAMPS (a 40 m ball leaves
  //    at GROUND_PASS_MAX_SPEED and arrives hot).
  //
  //    ⚠ NOT evaluated at the LEAD point, and that distinction was measured the
  //    hard way. The sim aims at the lead point but weights the pass for the man,
  //    so the ball routinely stops a few metres short of the lead point — which is
  //    CORRECT, because the receiver is running onto it. Scoring that gap as an
  //    under-hit charged a penalty on every pass to a moving forward and cut
  //    passing from 131 to 78 decisions in a 2-minute AI-vs-AI match.
  travelForDistance(launch, d0, travelScratch);
  const arrivalSpeed = travelScratch.reached ? travelScratch.speed : 0;
  if (arrivalSpeed > AI_PASS_ARRIVAL_IDEAL) {
    const over = arrivalSpeed - AI_PASS_ARRIVAL_IDEAL;
    penalty += (over / (AI_PASS_ARRIVAL_MAX - AI_PASS_ARRIVAL_IDEAL)) * AI_SCORE_ARRIVAL_PENALTY;
  }

  // 2. OUT OF PLAY, and these are two different events with two different
  //    likelihoods, so they are priced separately.
  const runOut = groundRunOut(launch, travelScratch) + (lofted ? AI_PASS_LOFT_OVERRUN : 0);
  const exit = pitchExitDistance(b, px, pz, dx / dist, dz / dist);
  if (dist > exit) {
    // (a) THE BALL IS BEING AIMED OFF THE PITCH. resolveAim leads the runner by
    //     up to THROUGH_BALL_LEAD_CAP_S of his own velocity, so a winger
    //     sprinting at 8 m/s is led twelve metres — straight down the touchline
    //     and out. This is not a risk, it is a throw-in with extra steps, and it
    //     is charged in full.
    penalty += AI_SCORE_OUT_RISK_PENALTY;
  } else if (runOut > exit) {
    // (b) It is aimed at a legal spot, but if the receiver does not kill it the
    //     ball runs on and out. That is CONDITIONAL on him missing it, so it is
    //     scaled by how likely that is — a ball still travelling when it reaches
    //     him gets through him (POSSESSION_CAPTURE_REL_SPEED), a ball down to
    //     walking pace does not. `travelScratch` still holds the arrival solve
    //     from step 1 above, so this costs nothing.
    const missRisk = clamp01(
      (arrivalSpeed - AI_PASS_ARRIVAL_IDEAL) / (POSSESSION_CAPTURE_REL_SPEED - AI_PASS_ARRIVAL_IDEAL),
    );
    penalty +=
      clamp01((runOut - exit) / runOut) * AI_SCORE_OUT_RISK_PENALTY *
      (AI_OUT_RISK_MISS_FLOOR + (1 - AI_OUT_RISK_MISS_FLOOR) * missRisk);
  }
  return penalty;
}

function emitPass(b: AiBrain, id: number, target: number, through: boolean): void {
  const p = b.world.players;
  const dist = distXZ(p.px[id]!, p.pz[id]!, p.px[target]!, p.pz[target]!);
  // Aim AT the man: resolveAim() (§4.2) does the leading for a through ball, so
  // the AI does not need — and must not have — its own lead model.
  b.decAimYaw[id] = aimWithError(b, id, datan2(p.pz[target]! - p.pz[id]!, p.px[target]! - p.px[id]!));
  b.decKick[id] = through ? KickIntent.ThroughBall : KickIntent.Pass;
  b.decCharge[id] = chargeForPassDistance(dist);
  b.decLift[id] = 0;
  b.decPassTarget[id] = target; // §3 - the receiver must know it is coming
  // Planting to pass: without this, Sprint+Pass silently becomes a DRIVEN pass
  // (§4.2) whose speed band this charge was not solved for, and every AI pass
  // made on the run would arrive ~8 m/s hot.
  b.decFlags[id] = b.decFlags[id]! & ~InputFlags.Sprint;
}

/**
 * A cross is a lofted pass by intent: from a wide+advanced spot the sim shapes
 * that loft as a whipped cross (ball.ts isCrossPosition, 35–45° of arc). Aim at
 * the box target — resolveAim leads him like any pass.
 *
 * ── THE CROSS THAT NEVER LEFT THE GROUND (fixed 2026-08-09) ──────────────────
 *
 * This function used to emit `KickIntent.Pass` and rely on `decLift =
 * AI_CROSS_LIFT (1.0)` to reach the lofted branch. It never did. `buildPass`
 * (ball.ts:1313) selects its branch from the ThroughBall FLAG —
 *
 *     const lofted = (input.flags & InputFlags.ThroughBall) !== 0;
 *
 * — and reads `liftBias` nowhere at all (only buildShot / buildHeader /
 * buildBicycle and the keeper's distribution do). So every AI cross was emitted
 * with `angleDeg = 0` and `kick.vy = 0`: a flat ground pass fired along the
 * byline. The comment that used to sit here asserted the opposite and had done
 * since it was written; `attack-variety.test.ts` only ever asserted
 * `liftBias > 0.8`, which was true and meaningless, so nothing caught it.
 *
 * It surfaced building the corner landing trail (render/cornerTrail.ts): a
 * predictor that integrates the sim's real ballistics drew a corner rolling
 * along the goal line, because that is genuinely what a corner did.
 *
 * `liftBias` stays at AI_CROSS_LIFT — it is still the honest statement of
 * intent, it is what the variety test reads, and buildShot does honour it, so a
 * cross that ever routes through the shot path keeps its arc.
 */
function emitCross(b: AiBrain, id: number, target: number): void {
  const p = b.world.players;
  const dist = distXZ(p.px[id]!, p.pz[id]!, p.px[target]!, p.pz[target]!);
  b.decAimYaw[id] = aimWithError(b, id, datan2(p.pz[target]! - p.pz[id]!, p.px[target]! - p.px[id]!));
  // ThroughBall is how a striker asks buildPass for AIR. It is the same branch
  // emitPass already uses for a long ball; a cross is a long ball into the box.
  b.decKick[id] = KickIntent.ThroughBall;
  b.decCharge[id] = chargeForPassDistance(dist);
  b.decLift[id] = AI_CROSS_LIFT;
  b.decPassTarget[id] = target; // §3 - a cross is aimed at a MAN too
  b.decFlags[id] = b.decFlags[id]! & ~InputFlags.Sprint;
}

function emitShot(b: AiBrain, id: number, aimX: number, aimZ: number, dist: number): void {
  const p = b.world.players;
  b.decAimYaw[id] = aimWithError(b, id, datan2(aimZ - p.pz[id]!, aimX - p.px[id]!));
  b.decKick[id] = KickIntent.Shoot;
  const t = remapClamped(dist, AI_SHOT_CHARGE_NEAR_DIST, b.params.shootRange, AI_SHOT_CHARGE_NEAR_T2, 1);
  b.decCharge[id] = SHOT_CHARGE_MIN_S + t * (SHOT_CHARGE_MAX_S - SHOT_CHARGE_MIN_S);
  // Keep it down close in, lift it from range (§4.1 lift bias).
  b.decLift[id] = remapClamped(
    dist, AI_SHOT_CHARGE_NEAR_DIST, b.params.shootRange, AI_SHOT_LIFT_NEAR, AI_SHOT_LIFT_FAR,
  );
  // Sprint+Shoot is a POWER shot (§4.3): more pace, harder to place. The AI does
  // not opt into that gamble implicitly just because it was running.
  b.decFlags[id] = b.decFlags[id]! & ~InputFlags.Sprint;
}

/** Where in the goal to aim: the side the keeper is further from. */
function shotAimZ(b: AiBrain, id: number): number {
  const p = b.world.players;
  const team = p.team[id]!;
  const post = GOAL_WIDTH * 0.5 * AI_SHOT_POST_FRAC2;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! === team) continue;
    if (b.world.positions[i] !== 'GK') continue;
    // A keeper you have not looked at cannot be aimed away from (§10.2).
    if (!canSee(b.world, id, p.px[i]!, p.pz[i]!)) break;
    return p.pz[i]! >= 0 ? -post : post;
  }
  // No keeper, or not seen: the near-post/far-post read is unavailable, so put
  // it where a hurried finish goes — toward the middle, biased off centre.
  return p.pz[id]! >= 0 ? -post * 0.5 : post * 0.5;
}

/**
 * Collect up to `params.passOptions` pass candidates into candId/candDist,
 * nearest first. Returns the count.
 *
 * THIS IS §10.3's "decision quality" KNOB, and it is worth being precise about
 * why it is honest. Easy AI evaluates 3 options and hard AI evaluates 12 — but
 * both evaluate them with the same scoring function, and both are limited to
 * teammates they can actually see. The easy AI is not made worse by being given
 * a penalty; it is worse because it never considers the switch of play that was
 * on. That is how a weaker player is actually weaker, and it cannot be detected
 * as cheating because nothing was taken away from it.
 */
function collectPassOptions(b: AiBrain, id: number): number {
  const p = b.world.players;
  const team = p.team[id]!;
  // ── THE SCAN (§10.2, and the biggest measured hole in this AI) ─────────────
  //
  // MEASURED, gate by gate, over every FORWARD teammate a carrier had: after the
  // head-up fix (58360f6) 39.5 % of them were STILL rejected for nothing but
  // sitting outside the 140° facing cone. Four in ten forward options were
  // invisible, and the carrier's alternative was to dribble or shield.
  //
  // A man with the ball at his feet and nobody near him has already looked. That
  // is what scanning is, and it is the single most-coached habit in the game — a
  // midfielder checks his shoulders before the ball arrives precisely so he does
  // NOT have to be facing a teammate to know he is there. Modelling "you cannot
  // pass to a man you have not turned toward" as an absolute is modelling a
  // player with no neck.
  //
  // So the carrier's PASS-OPTION cone widens by up to AI_CARRIER_SCAN_EXTRA_DEG,
  // and it closes back to the plain vision cone as he is closed down: under
  // pressure there is no time to look, which is exactly when a real player plays
  // the ball he can see or loses it. This is a time budget, not clairvoyance —
  // and it is deliberately scoped to THIS function only. `canSee` still gates
  // everything else (reacting to a carrier, picking a shot, spotting a presser),
  // so the AI still cannot react to what is behind it.
  const scan =
    AI_CARRIER_SCAN_EXTRA_DEG * (1 - clamp01(pressureOn(b.world, id) / AI_CARRIER_SCAN_PRESSURE_MAX));
  const coneHalfRad = (AI_VISION_CONE_DEG + scan) * 0.5 * DEG2RAD;
  // The lay-off sector: wider than the scan cone but NOT 360°. See the lay-off
  // block below and AI_PASS_LAYOFF_CONE_DEG.
  const layoffHalfRad = AI_PASS_LAYOFF_CONE_DEG * 0.5 * DEG2RAD;
  gateScanDeg = AI_VISION_CONE_DEG + scan;
  gateMates = 0;
  gateTooClose = 0;
  gateTooFar = 0;
  gateCone = 0;
  gateOffside = 0;
  gateLayoff = 0;
  let n = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === id || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    if (b.world.positions[i] === 'GK') continue; // back-passes are a §5.5 trap
    gateMates++;
    const d = distXZ(p.px[id]!, p.pz[id]!, p.px[i]!, p.pz[i]!);
    if (d < AI_PASS_MIN_DISTANCE) { gateTooClose++; continue; }
    // Teammates use the extended awareness radius (you KNOW your own shape;
    // see AI_TEAMMATE_AWARENESS_RADIUS) but the same facing cone as canSee,
    // widened by the scan above — without this every cross-pitch switch was
    // beyond the 35m bubble and structurally unplayable.
    if (d > AI_TEAMMATE_AWARENESS_RADIUS) { gateTooFar++; continue; }
    // ── THE LAY-OFF (§10.2, AI_PASS_LAYOFF_RADIUS) ────────────────────────────
    //
    // MEASURED, over 8 seeds x 5 sim-min with a per-gate tally on every outfield
    // teammate a carrier had: 69.7 % of them were rejected here, for nothing but
    // sitting outside the facing cone. Not 39.5 % of FORWARD options (session
    // 41's number) — SEVENTY PER CENT OF EVERY OPTION. Only 0.98 per decision
    // were lost to a blocked lane, so "the carrier has nobody to pass to" is
    // overwhelmingly a facing problem, and the carrier held the ball on 64.3 % of
    // all his decisions.
    //
    // The cone is right about the far ball and wrong about the near one. Setting
    // it back to a supporting player 10 m behind you is one of the most common
    // passes in football and it is routinely played WITHOUT LOOKING: he was in
    // your peripheral vision two seconds ago, he is calling, and you can hear
    // where he is. Modelling that as unavailable models a player who has to turn
    // his whole body round to remember that his midfield exists — and it is
    // precisely the pass a man who has run into a dead end needs.
    //
    // So INSIDE AI_PASS_LAYOFF_RADIUS the cone widens to AI_PASS_LAYOFF_CONE_DEG
    // — over the shoulder, NOT all the way round. Beyond the radius the ordinary
    // cone applies in full: a switch to the far touchline behind you is a genuine
    // information problem and stays unavailable.
    //
    // ⚠ AND THE SECTOR LIMIT IS LOAD-BEARING, not a nicety. A flat 360° exemption
    // inside the radius breaks `pass-to-human.test.ts`'s "will not blind-pass to a
    // human directly behind the carrier" — an assertion whose entire purpose is
    // that co-op preference must not defeat §10.2 — and it also broke the same
    // file's four-packed-teammates case, because on 'easy' (passOptions = 3) the
    // three men clustered DEAD BEHIND crowded the open forward man out of the
    // nearest-N candidate list entirely, and the carrier then passed to nobody.
    // Both failures are the same defect: a man behind your back is not a man you
    // know about, however close he is. The sector keeps him out by construction.
    //
    // Nothing else is relaxed — the lane-block gate, the offside gate and the
    // backward-pass penalty all still apply to a lay-off, so this adds only
    // options that were safe to play anyway.
    //
    // ⚠ MEASURED AND REJECTED: also demanding a FULLY safe corridor
    // (AI_PASS_LANE_SAFE_RADIUS) for a blind lay-off, on the reasoning that you
    // may not thread a needle you have not looked at. It sounds right and it is
    // wrong here: over 8 seeds x 5 sim-min it did not recover the completion it
    // was aimed at (53.1 % -> 50.7 %) and it turned the lay-offs it refused into
    // DRIBBLES — the carrier's dribble share went 4.6 % -> 28.6 % and pass
    // attempts collapsed 573 -> 408. The sim already prices lane width twice
    // (AI_SCORE_PASS_LANE_SAFETY_WEIGHT ramps the score from the block radius to
    // the safe radius, and passExecutionPenalty charges the arrival), so a third
    // hard gate on the same quantity only removes the option.
    const inCone = withinCone(b.world, id, p.px[i]!, p.pz[i]!, coneHalfRad);
    const layoff =
      !inCone && d <= AI_PASS_LAYOFF_RADIUS &&
      withinCone(b.world, id, p.px[i]!, p.pz[i]!, layoffHalfRad);
    if (layoff) gateLayoff++;
    if (!inCone && !layoff) {
      gateCone++;
      continue;
    }
    // A man STANDING OFFSIDE at kick time is flagged the moment he plays the
    // ball — never a candidate (same projection the call gate used; +∞ when
    // offside is disabled or no line can be drawn). The old 35m awareness
    // bubble hid this gap: a far-beyond-the-line man was simply invisible.
    if (b.attackSign * p.px[i]! > b.onsideLimitProj) { gateOffside++; continue; }
    candId[n] = i;
    candDist[n] = d;
    n++;
  }

  // Insertion sort by distance. n ≤ 21 and this runs at 10Hz — the constant
  // factor of anything cleverer would cost more than it saves.
  for (let i = 1; i < n; i++) {
    const di = candDist[i]!;
    const ii = candId[i]!;
    let j = i - 1;
    while (j >= 0 && candDist[j]! > di) {
      candDist[j + 1] = candDist[j]!;
      candId[j + 1] = candId[j]!;
      j--;
    }
    candDist[j + 1] = di;
    candId[j + 1] = ii;
  }

  return n < b.params.passOptions ? n : b.params.passOptions;
}

/**
 * Pinned deep with nothing on: PROGRESS the ball instead of shielding it back.
 *
 * A pressed team that just shields at its own feet for a whole match is the "they
 * are just running like idk" match — nobody scores, the other side hacks. Real
 * football clears the lines. Priority: a forward outlet we can see → carry into
 * space ahead → last-resort ball upfield. Returns true if it took an action.
 */
function progressForwardClearance(b: AiBrain, id: number): boolean {
  const p = b.world.players;
  const team = p.team[id]!;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  const half = halfLengthOf(b);

  // 1) Best forward outlet we can actually see. Lane strictness is relaxed — this
  //    is a clearance, not a killer pass; the job is to get it up the pitch.
  let best = -1;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === id || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    if (b.world.positions[i] === 'GK') continue;
    const prog = (b.attackSign * (p.px[i]! - px)) / half;
    if (prog <= 0) continue; // forward only
    const d = distXZ(px, pz, p.px[i]!, p.pz[i]!);
    if (d < AI_PASS_MIN_DISTANCE) continue;
    if (!canSee(b.world, id, p.px[i]!, p.pz[i]!)) continue;
    // Most forward, but not miles away — and NOT into touch. A clearance is
    // exactly the decision that used to hoof the ball off the pitch, so the same
    // execution test the option scorer runs applies here too (its scale is
    // deliberately larger than `prog`: no amount of progress is worth a
    // throw-in to the other side).
    const s = prog - d / (half * 2) - passExecutionPenalty(b, id, i, d > AI_THROUGH_BALL_MIN_DISTANCE);
    if (s > bestScore) {
      bestScore = s;
      best = i;
    }
  }
  if (best >= 0) {
    const d = distXZ(px, pz, p.px[best]!, p.pz[best]!);
    emitPass(b, id, best, d > AI_THROUGH_BALL_MIN_DISTANCE);
    return true;
  }

  // 2) No outlet in view — carry into space ahead if there is any.
  b.influence.bestCellNear(
    px + b.attackSign * AI_DRIBBLE_LOOKAHEAD,
    pz,
    AI_SUPPORT_SEARCH_RADIUS * 0.5,
    b.attackSign,
    space,
  );
  if (b.attackSign * (space.x - px) > AI_CLEARANCE_MIN_FORWARD) {
    seek(b, id, space.x, space.z);
    faceSeek(b, id);
    setSprint(b, id, true);
    return true;
  }

  // 3) Last resort: put a foot through it upfield. A midfield turnover keeps the
  //    game flowing; shielding at your own corner flag does not.
  b.decAimYaw[id] = aimWithError(b, id, clearanceAimYaw(b, id));
  b.decKick[id] = KickIntent.Pass;
  b.decCharge[id] = chargeForPassDistance(AI_PASS_MAX_COMFORTABLE_DISTANCE);
  b.decFlags[id] = b.decFlags[id]! & ~InputFlags.Sprint;
  return true;
}

function decideOnBall(b: AiBrain, id: number): void {
  const p = b.world.players;
  const w = b.world;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  const team = p.team[id]!;
  const half = halfLengthOf(b);
  const goalX = b.attackSign * half;
  const pressure = pressureOn(w, id);

  let bestScore = AI_OPTION_MIN_SCORE;
  let bestKind = 0; // 0 hold, 1 shoot, 2 pass, 3 dribble
  let bestTarget = -1;
  let bestThrough = false;

  // ── Shoot ───────────────────────────────────────────────────────────────
  const aimZ = shotAimZ(b, id);
  const distGoal = distXZ(px, pz, goalX, aimZ);
  const inBox = inAttackingBox(b, id);
  // In the box you get extra reach — a half-chance in the six-yard area is on.
  const shootRange = b.params.shootRange + (inBox ? AI_SHOT_BOX_RANGE_BONUS : 0);
  if (distGoal <= shootRange) {
    const clear = laneClearance(w, team, px, pz, goalX, aimZ, id, -1);
    // A blocked lane FLOORS the shot rather than zeroing it — a striker shoots
    // through a crowd instead of endlessly looking for a cleaner pass that never
    // comes. The old `clamp01(clear/R)` made a busy box un-shootable (measured:
    // 0-2 shots and 0 goals a match despite 10+ box entries).
    const laneFactor =
      AI_SHOT_LANE_FLOOR + (1 - AI_SHOT_LANE_FLOOR) * clamp01(clear / AI_SHOT_LANE_BLOCK_RADIUS);
    const rangeFactor = 1 - AI_SHOT_RANGE_FALLOFF * (distGoal / shootRange);
    let s = AI_SCORE_SHOOT_BASE * rangeFactor * laneFactor;
    // In the box you SHOOT — you do not try to walk it in.
    if (inBox) s += AI_SCORE_SHOOT_BOX_BONUS;
    if (s > bestScore) {
      bestScore = s;
      bestKind = 1;
    }
  }


  // ── Pass ────────────────────────────────────────────────────────────────
  const n = collectPassOptions(b, id);
  let dbgLaneBlocked = 0; // §8 telemetry only
  let dbgUsableForward = 0;
  let dbgBestPassScore = Number.NEGATIVE_INFINITY;
  let dbgBestPassProgress = 0;
  for (let c = 0; c < n; c++) {
    const t = candId[c]!;
    // §8 team CIRCULATE — keep the ball among the BOTS: skip the human as a target
    // (he is excluded unless no bot is available, in which case circulate simply
    // finds no option and the carrier falls through to a normal decision).
    if (b.circulateActive && b.humanMask[t] === 1) continue;
    const tx = p.px[t]!;
    const tz = p.pz[t]!;
    const d = candDist[c]!;
    const clear = laneClearance(w, team, px, pz, tx, tz, id, t);
    if (clear < AI_PASS_LANE_BLOCK_RADIUS) {
      dbgLaneBlocked++;
      continue; // somebody is in the way
    }

    // Progress up the pitch, normalized to a half-length.
    const progress = clamp((b.attackSign * (tx - px)) / half, -1, 1);
    const receiverSpace = normControl(b.influence.controlAt(tx, tz));
    // A long ball is a worse ball even when it progresses play — it is slower to
    // arrive, easier to read, and (see passSpeedForDistance) can outrun the
    // trappable-speed band. Without this the AI hoofs it forward every time.
    const distPenalty = clamp01(d / AI_PASS_MAX_COMFORTABLE_DISTANCE) * AI_SCORE_PASS_DISTANCE_PENALTY;
    // Progression is rewarded EXPLICITLY and non-linearly so a ball that advances
    // toward goal beats the safety of a square pass — the stock scorer's linear
    // progress term rarely did, so the AI recycled sideways and never built an
    // attack (measured: forward-pass ratio ~40% for the trailing team).
    let progTerm = progress * AI_SCORE_PASS_PROGRESS_WEIGHT;
    if (progress > 0) {
      progTerm += progress * progress * AI_SCORE_PASS_PROGRESS_BONUS;
      // A pass that lands the receiver in the final third is a chance-creator.
      if ((b.attackSign * tx) / half > AI_FINAL_THIRD_FX) progTerm += AI_SCORE_PASS_FINAL_THIRD_BONUS;
    } else {
      // Square/backward balls are gently discouraged — look forward first.
      progTerm += progress * AI_SCORE_BACKWARD_PASS_PENALTY;
    }
    // Co-op preference (§13): a moderate nudge toward the human teammate, added
    // only here — AFTER the lane-block continue above and the see/offside gates
    // in collectPassOptions — so it can never drag a pass through a covered lane
    // or to an unseen/offside man, only tip a genuine near-tie the human's way.
    const humanBonus = b.humanMask[t] === 1 ? AI_SCORE_PASS_HUMAN_BONUS : 0;
    // Call-for-pass (§8): a STRONG preference for a teammate actively calling.
    // Applied after the lane-block continue above, and gated on the caller being
    // ONSIDE (attack-axis projection at or behind the second-last defender line),
    // so an OFFSIDE or lane-BLOCKED call is silently not obeyed rather than
    // illegally forced — a valid call wins the decision and is played within a
    // cycle; an invalid one falls through to the normal scorer.
    const callerOnside = b.attackSign * tx <= b.onsideLimitProj;
    const callBonus = b.callActive[t] === 1 && callerOnside ? AI_SCORE_PASS_CALL_BONUS : 0;
    // §8 team CIRCULATE — a strong nudge to feed a BOT teammate (never the human)
    // when the human has asked the team to keep the ball moving. Same gates as any
    // pass (lane-block/see/offside above), so an unsafe circulate is not obeyed.
    const circulateBonus =
      b.circulateActive && b.humanMask[t] !== 1 && callerOnside ? AI_SCORE_PASS_CIRCULATE_BONUS : 0;
    // Reward breathing room in the lane so a clean corridor beats a needle that
    // barely cleared the block gate (§8: safer passes complete). Ramps 0→1 from
    // the block radius to AI_PASS_LANE_SAFE_RADIUS.
    const laneSafety = clamp01(
      (clear - AI_PASS_LANE_BLOCK_RADIUS) / (AI_PASS_LANE_SAFE_RADIUS - AI_PASS_LANE_BLOCK_RADIUS),
    );
    // Switch of play (§4.7): a long CROSS-pitch ball to a receiver in genuine
    // space escapes the distance penalty that otherwise kills every diagonal —
    // scaled by how far it actually travels laterally × the space waiting on
    // the far side, and never for a backward ball. (Offside-positioned
    // targets never reach this scorer — collectPassOptions gates them.)
    const lateral = Math.abs(tz - pz);
    const switchBonus =
      lateral > AI_SWITCH_MIN_LATERAL && progress > -0.05
        ? clamp01((lateral - AI_SWITCH_MIN_LATERAL) / AI_SWITCH_MIN_LATERAL) *
          receiverSpace * AI_SCORE_PASS_SWITCH_WEIGHT
        : 0;
    // A long forward ball is played as a THROUGH ball: lofted, and led onto the
    // runner by resolveAim (§4.2). Decided here, before scoring, because the two
    // are executed differently and therefore have to be JUDGED differently — a
    // lofted ball bounces on, which is why the out-risk term below charges it
    // AI_PASS_LOFT_OVERRUN of extra run.
    let asThrough = d > AI_THROUGH_BALL_MIN_DISTANCE && progress > 0;
    // WILL IT ARRIVE PLAYABLE, AND WILL IT STAY ON THE PITCH? (See
    // passExecutionPenalty — the two questions the scorer never asked, and
    // between them 61 % of every ball this AI put out of play.)
    let execPenalty = passExecutionPenalty(b, id, t, asThrough);
    // THE FORM IS PART OF THE DECISION, not a consequence of the distance. A
    // through ball is lofted, so it lands and bounces on — MEASURED after the
    // first pass of these fixes, through-balls were still 40 % of every ball put
    // out of play, the single largest remaining cause. When the loft is what
    // takes the ball off the pitch, the same ball played along the floor is the
    // better football: it keeps the length, loses the overrun, and stays in.
    if (asThrough) {
      const flat = passExecutionPenalty(b, id, t, false);
      if (flat < execPenalty) {
        execPenalty = flat;
        asThrough = false;
      }
    }
    const s =
      AI_SCORE_PASS_BASE + progTerm + receiverSpace * AI_SCORE_PASS_SPACE_WEIGHT -
      distPenalty + humanBonus + callBonus + circulateBonus +
      laneSafety * AI_SCORE_PASS_LANE_SAFETY_WEIGHT + switchBonus - execPenalty;
    // "Usable" = forward AND above the play floor the scorer itself applies —
    // with the 60m teammate radius, a far diagonal the distance penalty
    // rightly rejects is now in the loop, and counting it as "usable" made
    // the hold-with-open-forward invariant fire on correct behaviour.
    if (progress > 0.05 && s > AI_OPTION_MIN_SCORE) dbgUsableForward++;
    if (s > dbgBestPassScore) {
      dbgBestPassScore = s;
      dbgBestPassProgress = progress;
    }
    if (s > bestScore) {
      bestScore = s;
      bestKind = 2;
      bestTarget = t;
      bestThrough = asThrough;
    }
  }

  // ── Cross (§4.7) ────────────────────────────────────────────────────────
  // Wide + advanced (the sim's own cross geometry — buildPass shapes a loft
  // from here as a whipped cross): a deliberately lofted ball to a teammate
  // attacking the box. Candidates are the same vision/offside-gated set as
  // passes but deliberately NOT lane-gated — a cross flies over the traffic
  // that blocks a ground ball, which is the entire point of playing one.
  let bestCross = -1;
  const wide = Math.abs(pz) > w.config.pitchWidth * 0.5 * CROSS_WIDE_FRAC;
  const advanced = b.attackSign * px > half * 0.5;
  if (wide && advanced) {
    let bestCrossScore = AI_OPTION_MIN_SCORE;
    for (let c = 0; c < n; c++) {
      const t = candId[c]!;
      if (b.circulateActive && b.humanMask[t] === 1) continue;
      const tx = p.px[t]!;
      const tz = p.pz[t]!;
      // The target must be attacking the box, meaningfully infield of the
      // carrier, and far enough that a loft makes sense.
      if (b.attackSign * tx < half - PENALTY_AREA_LENGTH - 4) continue;
      if (Math.abs(tz) > PENALTY_AREA_WIDTH * 0.5 + 2) continue;
      if (Math.abs(tz - pz) < 8 || candDist[c]! < 10) continue;
      const targetSpace = normControl(b.influence.controlAt(tx, tz));
      const s = AI_SCORE_CROSS_BASE + targetSpace * AI_SCORE_CROSS_TARGET_SPACE_WEIGHT;
      if (s > bestCrossScore) {
        bestCrossScore = s;
        bestCross = t;
      }
    }
    if (bestCross >= 0 && bestCrossScore > bestScore) {
      bestScore = bestCrossScore;
      bestKind = 4;
      bestTarget = bestCross;
    }
  }

  // ── Dribble ─────────────────────────────────────────────────────────────
  const aheadX = px + b.attackSign * AI_DRIBBLE_LOOKAHEAD;
  const spaceAhead = normControl(b.influence.controlAt(aheadX, pz));
  {
    let s =
      AI_SCORE_DRIBBLE_BASE +
      spaceAhead * AI_SCORE_DRIBBLE_SPACE_WEIGHT -
      pressure * AI_SCORE_PRESSURE_PENALTY;
    // Unpressed with space in front AND no forward pass already winning: carry
    // the ball AT the goal rather than hold. Gating on `bestKind !== 2` keeps it
    // pass-FIRST — a visible open forward is still played in (§10.2 vision gate)
    // — while turning "reaches the final third" into "gets into the box" whenever
    // there is nobody better to give it to.
    if (bestKind !== 2 && pressure < AI_DRIBBLE_UNPRESSED_MAX && spaceAhead > 0) {
      s += AI_SCORE_DRIBBLE_FORWARD_BONUS;
    }
    if (s > bestScore) {
      bestScore = s;
      bestKind = 3;
    }
  }

  if (onBallDebug !== null) {
    onBallDebug.push({
      tick: w.tick,
      id,
      team,
      kind: bestKind,
      score: bestScore,
      pressure,
      passSeen: n,
      passLaneBlocked: dbgLaneBlocked,
      usableForward: dbgUsableForward,
      bestPassScore: dbgBestPassScore,
      bestPassProgress: dbgBestPassProgress,
      mates: gateMates,
      rejTooClose: gateTooClose,
      rejTooFar: gateTooFar,
      rejCone: gateCone,
      rejOffside: gateOffside,
      layoffExempt: gateLayoff,
      scanDeg: gateScanDeg,
    });
  }

  // ── Act ─────────────────────────────────────────────────────────────────
  const canKick = b.kickCooldown[id]! <= 0;

  if (bestKind === 1 && canKick) {
    seek(b, id, goalX, aimZ);
    emitShot(b, id, goalX, aimZ, distGoal);
    return;
  }
  if (bestKind === 2 && canKick && bestTarget >= 0) {
    seek(b, id, p.px[bestTarget]!, p.pz[bestTarget]!);
    emitPass(b, id, bestTarget, bestThrough);
    return;
  }
  if (bestKind === 4 && canKick && bestTarget >= 0) {
    seek(b, id, p.px[bestTarget]!, p.pz[bestTarget]!);
    emitCross(b, id, bestTarget);
    return;
  }
  // A strike this man DECIDED on, blocked only because he kicked moments ago:
  // CARRY it while the cooldown bleeds off. Falling through to the shield branch
  // below — which is what used to happen — turned every cooldown-blocked pass
  // into "stop, turn away from the nearest opponent, walk backwards", i.e. the
  // dwell the owner reported, and (before the head-up fix) blinded him to the
  // very forward option he had just chosen. MEASURED before the strike buffer
  // landed: 59 % of all shoot/pass/cross decisions ended here.
  if ((bestKind === 1 || bestKind === 2 || bestKind === 4) && !canKick) bestKind = 3;
  if (bestKind === 3) {
    b.influence.bestCellNear(aheadX, pz, AI_SUPPORT_SEARCH_RADIUS * 0.5, b.attackSign, space);
    seek(b, id, space.x, space.z);
    faceSeek(b, id);
    // Knock it into space only when there IS space — the ball cap (§3.3) makes
    // this the difference between beating a man and running into him.
    const breaking = spaceAhead > 0 && pressure < 0.5;
    setSprint(b, id, breaking);
    // BOOST THE BREAK — but only a break that is already ON.
    //
    // The first cut fired this at pressure > 0.45, i.e. almost exactly where
    // `breaking` is FALSE: a carrier being closed down, told to sprint AND
    // boost straight into the man pressing him. MEASURED, it broke
    // situational-shape R5 — team 0 chasing the game late sat at mean x
    // -20.6 against -11.2 when winning, because carriers kept boosting into
    // contact and losing it deep. Same condition as the sprint, with a real
    // amount of space rather than any: he is clear, he has the ball, the gear
    // makes the break stick.
    if (breaking && spaceAhead > AI_BOOST_SPACE_MIN) setBoost(b, id, 1);
    return;
  }

  // Nothing scored above threshold. Pinned deep in our own third AND pressed?
  // Progress the ball rather than shield backward into trouble — a camped team
  // shielding at its own feet is exactly the aimless football the user flagged.
  const fxOwn = (b.attackSign * px) / half;
  if (fxOwn < AI_BUILD_UP_FX && pressure > AI_STUCK_PRESSURE && canKick) {
    if (progressForwardClearance(b, id)) return;
  }

  // ⚠ IN TROUBLE? GIVE IT TO SOMEBODY (owner, 2026-08-16: "when they are in
  // trouble pass the ball or dribble"). Everything above scores FORWARD
  // options - collectPassOptions gates on the facing cone and on progress -
  // so a man being closed down with no forward option fell through to the
  // shield and stayed there, backing up with the ball. A real player under
  // pressure plays the SAFE ball: any teammate, any direction, as long as the
  // lane is clean and he is not passing into the presser. Sideways and
  // backwards are legitimate football; walking it out of play is not.
  if (pressure >= AI_TROUBLE_PRESSURE && canKick) {
    let bailTarget = -1;
    let bailScore = -Infinity;
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (i === id || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
      if (p.team[i]! !== team || p.knockdownTimer[i]! > 0) continue;
      const d = distXZ(px, pz, p.px[i]!, p.pz[i]!);
      if (d < AI_BAIL_MIN_M || d > AI_BAIL_MAX_M) continue;
      // Never into a marked man, and never through the presser: the lane has
      // to be as clean as any pass this file would otherwise play.
      if (laneClearance(w, team, px, pz, p.px[i]!, p.pz[i]!, id, i)
        < AI_PASS_LANE_BLOCK_RADIUS) continue;
      // Prefer a mate who is FREER than we are, then the shorter ball.
      const theirPressure = pressureOn(w, i);
      if (pressure - theirPressure < AI_BAIL_RELIEF) continue;  // not an escape
      const score = (pressure - theirPressure) * 2 - d * 0.05;
      if (score > bailScore) { bailScore = score; bailTarget = i; }
    }
    if (bailTarget >= 0) {
      seek(b, id, p.px[bailTarget]!, p.pz[bailTarget]!);
      emitPass(b, id, bailTarget, false);
      return;
    }
  }

  // Hold: shield it. Put your body between the nearest opponent and the ball by
  // moving directly away from him. There is no shielding mechanic to invoke —
  // this IS the mechanic, because stepPlayerCollisions() (§3.3, §4.4) resolves
  // the shoulder contest continuously for whoever is in the way.
  let ax = -b.attackSign;
  let az = 0;
  let nearest = Number.POSITIVE_INFINITY;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === team) continue;
    const d = distXZ(px, pz, p.px[i]!, p.pz[i]!);
    if (d < nearest && d > 1e-3) {
      nearest = d;
      ax = (px - p.px[i]!) / d;
      az = (pz - p.pz[i]!) / d;
    }
  }
  // ⚠ A SHIELD SHOULD NOT WALK THE BALL BACKWARD (owner, 2026-08-21/23: "the
  // bots dribble the ball backward"). "Away from the nearest opponent" is
  // exactly the carrier's own goal direction whenever that opponent is
  // goal-side of him — which the very next fix down (facing) already
  // documents as the COMMON case, not the exception: "the nearest opponent,
  // who is almost always goal-side". So the shield vector was routinely
  // retreating toward his own goal by construction, not merely by an
  // occasional bad geometry roll. MEASURED (backward-shield-diagnostic.ts,
  // 5 seeds x 3600 ticks, normal AI): 66.2% of samples where a carrier had
  // an opponent within 4m netted BACKWARD movement, mean backward
  // component 0.41 — a real, common pattern, not an edge case.
  //
  // ⚠ NOT a hard zero — a first cut clamping the forward-axis component to
  // exactly 0 measurably broke `situational-shape` R5 (a losing side
  // stopped out-committing the same side winning; team 0 two-down went from
  // sitting 2.2m FORWARD of two-up to sitting 15.5m BEHIND it). A carrier
  // with genuinely zero backward option under close pressure gets
  // dispossessed more, not less — a real player DOES take a half-step back
  // to buy the half-second that turns into a recycled pass, and removing
  // that entirely cost more in turnovers than the backward drift itself was
  // worth. AI_SHIELD_MAX_BACKWARD keeps a fraction of the raw "away" pull
  // instead: still shields sideways in full (lateral untouched), still
  // reads as evasive under real pressure, but no longer marches backward at
  // full pace as its default reflex.
  {
    const fwd = ax * b.attackSign;
    if (fwd < 0) {
      ax -= fwd * (1 - AI_SHIELD_MAX_BACKWARD) * b.attackSign;
    }
  }
  // ⚠ A SHIELD MUST NOT WALK THE BALL OUT OF PLAY (owner, 2026-08-16: "when
  // bots where being chased for the ball they are running backwards and ...
  // taking the ball outside"). `ax/az` points straight away from the nearest
  // presser, and a presser who has come from the middle points it at the
  // TOUCHLINE — so a shielding carrier reversed until the ball crossed the
  // line and handed over a throw. The escape direction is now BENT BACK
  // TOWARD THE PITCH as he nears any boundary, smoothly, so shielding by the
  // corner flag turns into shielding along it.
  {
    const halfW = w.config.pitchWidth * 0.5;
    const halfL = w.config.pitchLength * 0.5;
    // 0 in the middle of the pitch, 1 at the line: how much the boundary wins.
    const outZ = Math.max(0, (Math.abs(pz) - (halfW - AI_TOUCHLINE_GUARD_M)) / AI_TOUCHLINE_GUARD_M);
    const outX = Math.max(0, (Math.abs(px) - (halfL - AI_TOUCHLINE_GUARD_M)) / AI_TOUCHLINE_GUARD_M);
    if (outZ > 0) az = az * (1 - outZ) - Math.sign(pz) * outZ;
    if (outX > 0) ax = ax * (1 - outX) - Math.sign(px) * outX;
    const len = Math.sqrt(ax * ax + az * az);
    if (len > 1e-4) { ax /= len; az /= len; }
  }
  seek(b, id, px + ax * AI_DRIBBLE_LOOKAHEAD * 0.5, pz + az * AI_DRIBBLE_LOOKAHEAD * 0.5);
  // ...but keep your HEAD UP. faceSeek() here pointed him down the shield vector
  // — directly AWAY from the nearest opponent, who is almost always goal-side, so
  // a shielding carrier ended up facing his own goal. collectPassOptions gates
  // every candidate on canSeeDirection (§10.2's facing cone), so the next 10Hz
  // decision could not see a single forward teammate, and the only thing left
  // scoring was another shield: a self-reinforcing blindness loop.
  //
  // MEASURED (seed 7, 36,000-tick soak, gate-by-gate over every forward teammate
  // a carrier had): 63.6% of them were outside the 140° cone — by far the biggest
  // reason a forward pass was never even scored (lane blocks were only 7.5%).
  //
  // Aiming upfield is not a vision cheat and it is not free: it TRADES away the
  // sight of what is behind him, which is exactly the trade a real player makes
  // when he lifts his head. It also cannot move the ball into the presser — the
  // dribble knock runs along velocity blended with the MOVE axes, never along yaw
  // (ball.ts), and the shield seek above is unchanged — and it cannot strike the
  // ball, because a hold emits no kick intent.
  b.influence.bestCellNear(aheadX, pz, AI_SUPPORT_SEARCH_RADIUS * 0.5, b.attackSign, space);
  const hx = space.x - px;
  const hz = space.z - pz;
  b.decAimYaw[id] =
    hx * hx + hz * hz > 1e-6 ? datan2(hz, hx) : (b.attackSign > 0 ? 0 : PI);
}

const onBall: BtNode = (b, id) => {
  decideOnBall(b, id);
  return Bt.Success;
};

// ---------------------------------------------------------------------------
// Team has the ball: make a run / provide an angle / hold shape (§10.1)
// ---------------------------------------------------------------------------

/**
 * WHERE TO SHOW FOR THE BALL — the offer point. (§10.1, §10.2)
 *
 * ── The measurement this exists to answer, and the one it does NOT ──────────
 *
 * 65.4 % of the nine outfield teammates a carrier had were rejected as pass
 * options for ONE reason: outside his facing cone (chance-creation.test.ts, 8
 * seeds x 5 sim-min). The previous session proved widening the cone further
 * makes the decision WORSE, and named the remaining lever: move the men.
 *
 * ⚠ It only half works, and the AI_OFFER_* block in constants.ts holds the
 * trace. The point this function returns is inside the carrier's real scan cone
 * 49.8 % of the time against the anchor's 37.9 % — the geometry is sound — but
 * the player running at it is inside only 38.0 %, because he sits a mean 8.6 m
 * short of his own target and a carrier's facing turns faster than a footballer
 * moves. What measurably moved is engagement and retention, not the carrier's
 * cone-rejection rate. Keep this function for what it demonstrably does.
 *
 * ── What it computes ────────────────────────────────────────────────────────
 *
 * Everything is measured from this player's FORMATION ANCHOR, never from where
 * he currently is. The anchor is stable at 5Hz while the player is mid-run, so
 * the sector edge does not chase its own tail, and the SIDE he offers on is
 * decided by where he belongs rather than by which way he happens to be
 * drifting. That is the whole anti-jitter design: the carrier would have to turn
 * ~90° for the side to flip.
 *
 * Find the space near where he belongs, THEN clamp that point into the sector.
 * If it is already inside ±AI_OFFER_CONE_HALF_DEG of the carrier's facing he is
 * visible and nothing moves. If it is outside, it swings onto the NEAR sector
 * edge AT EXACTLY THE SAME RADIUS — he rotates, he does not close in — and the
 * drift clamp then keeps him inside one line gap of his slot.
 *
 * ⚠ Radius preservation is load-bearing. The obvious version of this function
 * moves him to a fixed "good pass distance", and that is a swarm: ten players
 * all converging to 15 m of the ball is exactly what
 * ai/loose-ball-organisation.test.ts exists to prevent and what would destroy
 * the pass-based game the owner asked for.
 *
 * Divide guards: `r` (the point may sit exactly on the carrier — no bearing
 * exists, so there is nothing to rotate onto) and `dd` (only entered when
 * dd > AI_OFFER_MAX_ANCHOR_DRIFT).
 */
function offerPoint(b: AiBrain, id: number, out: SpacePoint): void {
  const p = b.world.players;
  const carrier = b.world.ball.possessedBy;
  const ax = b.shapeX[id]!;
  const az = b.shapeZ[id]!;

  // 1. THE SPACE FIRST. Where, near where he belongs, is there room? Last
  //    cycle's seek is the keep-point, so the cell holds steady instead of
  //    hopping between adjacent cells every rebuild (the Support anti-roam).
  b.influence.bestCellNear(
    ax, az, AI_OFFER_SEARCH_RADIUS, b.attackSign, out,
    b.decSeekX[id]!, b.decSeekZ[id]!, AI_SUPPORT_CELL_KEEP_BONUS,
  );

  if (carrier < 0 || carrier === id) return;

  // 2. THE SECTOR LAST — and this order is the fix, not a detail. The first
  //    version rotated the anchor and then ran the influence search around the
  //    result, and the search put him straight back out: a 7 m disc at a 20 m
  //    radius is ±20° of bearing, which is most of the margin the rotation had
  //    just bought. MEASURED (probe, 3 seeds x 2 min): the seek TARGET was
  //    inside the carrier's real scan cone only 37.0 % of the time even though
  //    the geometry aims at the edge by construction. Clamping after the search
  //    cannot be undone by anything.
  const cx = p.px[carrier]!;
  const cz = p.pz[carrier]!;
  const dx = out.x - cx;
  const dz = out.z - cz;
  const r = Math.sqrt(dx * dx + dz * dz);
  // r == 0: the point sits on the carrier, so no bearing exists and there is
  // nothing to rotate onto. (Guards the divide inside dcos/dsin's radius use.)
  if (r <= 1e-6) return;

  const half = AI_OFFER_CONE_HALF_DEG * DEG2RAD;
  const yaw = p.yaw[carrier]!;
  const delta = angleDelta(yaw, datan2(dz, dx));
  if (delta <= half && delta >= -half) return; // already showing; leave him be

  // AT EXACTLY THE RADIUS HE ALREADY HAD. Not a clamp into a "good passing
  // distance" band — that version pulls men toward the ball, and ten players
  // converging on 15 m of it is the swarm this must not be.
  const edge = yaw + (delta >= 0 ? half : -half);
  let tx = cx + r * dcos(edge);
  let tz = cz + r * dsin(edge);

  // 3. THE SHAPE STILL WINS. situational-shape.ts has already moved this anchor
  //    for the phase; an offer that ignores it is a player abandoning his
  //    position to stand where the ball happens to be looking.
  const ddx = tx - ax;
  const ddz = tz - az;
  const dd = Math.sqrt(ddx * ddx + ddz * ddz);
  if (dd > AI_OFFER_MAX_ANCHOR_DRIFT) {
    const s = AI_OFFER_MAX_ANCHOR_DRIFT / dd; // dd > 11 here, never a zero divide
    tx = ax + ddx * s;
    tz = az + ddz * s;
  }
  out.x = tx;
  out.z = tz;
}

const offBallAttack: BtNode = (b, id) => {
  const p = b.world.players;

  // SHOW FOR THE BALL (§10.1). Everyone who is not the carrier, not a forward
  // and not one of the two nearest used to stand on his anchor here. See
  // offerPoint: he moves onto the edge of the sector the carrier can see, at the
  // radius he already had.
  if (b.assignment[id] === Assignment.Offer) {
    offerPoint(b, id, space);
    seek(b, id, space.x, space.z);
    // Watch the ball, not the run. He is asking for a pass he must then receive,
    // and the carrier has to stay inside HIS cone for the arrival to be an
    // informed decision (§10.2) — the same reason a shape-holding player does it.
    const dz = b.world.ball.pz - p.pz[id]!;
    const dx = b.world.ball.px - p.px[id]!;
    b.decAimYaw[id] = dx * dx + dz * dz > 1e-6 ? datan2(dz, dx) : p.yaw[id]!;
    sprintIfFar(b, id);
    return Bt.Success;
  }

  if (b.assignment[id] === Assignment.Support) {
    // Find space near where you belong — not near the ball. A supporting run
    // that converges on the carrier is how AI teammates end up in a huddle.
    let cx = b.shapeX[id]!;
    const cz = b.shapeZ[id]!;
    let radius = AI_SUPPORT_SEARCH_RADIUS;
    const forward = isForwardPosition(b.world.positions[id]!);
    // In the final third a forward drives INTO the box to attack a cutback/cross
    // rather than hold a midfield shape — the difference between "near the box"
    // and "in the box for the tap-in".
    let czBox = cz;
    if (b.phase === TeamPhase.FinalThird && forward) {
      cx += b.attackSign * AI_SUPPORT_BOX_PUSH_FX * halfLengthOf(b);
      radius = AI_BOX_RUN_SEARCH_RADIUS;
      // Near/far-post split (§4.7): the box-attacking forwards fan out instead
      // of converging on one cutback cell. Deterministic rank among the team's
      // forwards (by slot id — static roster data): rank 0 attacks the NEAR
      // post (the ball's side), rank 1 the FAR post, rank 2+ keeps the central
      // edge-of-box cell. This is what splits two STs onto separate posts.
      //
      // ⚠ THIS DOES NOT WORK, AND THE FIX IS MEASURED BUT NOT LANDED. See the
      // "MEASURED AND REJECTED" block for AI_BOX_RUN_POST_MIN_Z in constants.ts:
      // the split is only a search CENTRE, and an 18 m radius around centres
      // ±4.6 m makes both forwards' arg-max land on the SAME cell (traced: both
      // sought z = −1.7 by t = 2 s), while rank-by-BALL-side additionally tells
      // them to CROSS OVER. `ai/attack-variety.test.ts`'s "forwards must fan
      // apart" is red because of this (2.69 m vs a required 4). Making it pass is
      // a ~20-line change that is fully understood — and it costs pass volume and
      // completions on every seed set tried, which the owner's pass-first
      // directive does not permit. The constants block holds the whole table.
      let rank = 0;
      const p2 = b.world.players;
      for (let j = 0; j < MAX_PLAYERS; j++) {
        if (j === id || p2.active[j] !== 1 || p2.sentOff[j] === 1) continue;
        if (p2.team[j] !== p2.team[id]) continue;
        if (!isForwardPosition(b.world.positions[j]!)) continue;
        if (j < id) rank++;
      }
      const nearSide = b.world.ball.pz >= 0 ? 1 : -1;
      if (rank === 0) czBox = nearSide * AI_BOX_RUN_POST_Z;
      else if (rank === 1) czBox = -nearSide * AI_BOX_RUN_POST_Z;
    } else if (b.phase === TeamPhase.Attack && forward) {
      // In the ATTACK phase (we hold it in midfield) a forward makes a run BEYOND
      // the ball to give the carrier a forward outlet — the run that turns
      // possession into territory (§4.7). He stays onside: the steer onside mask
      // holds him at the line, so this is a run TO the line, not past it. Without
      // it the carrier only ever sees square balls and play never leaves midfield.
      cx += b.attackSign * AI_SUPPORT_ATTACK_PUSH_FX * halfLengthOf(b);
    }
    // Pass last cycle's seek as the keep-point so the chosen cell holds steady
    // instead of hopping between adjacent cells every rebuild (anti-roam).
    b.influence.bestCellNear(
      cx, czBox, radius, b.attackSign, space,
      b.decSeekX[id]!, b.decSeekZ[id]!, AI_SUPPORT_CELL_KEEP_BONUS,
    );
    seek(b, id, space.x, space.z);
    faceSeek(b, id);
    sprintIfFar(b, id);
    return Bt.Success;
  }

  seek(b, id, b.shapeX[id]!, b.shapeZ[id]!);
  // Look at the ball while holding shape: it is what a player does, and it is
  // also what keeps the ball inside the vision cone so the next decision is an
  // informed one (§10.2). Facing is a real cost here, not a free lookaround.
  const dz = b.world.ball.pz - p.pz[id]!;
  const dx = b.world.ball.px - p.px[id]!;
  b.decAimYaw[id] = dx * dx + dz * dz > 1e-6 ? datan2(dz, dx) : p.yaw[id]!;
  sprintIfFar(b, id);
  return Bt.Success;
};

// ---------------------------------------------------------------------------
// Loose ball: should I contest? → pursue the intercept point (§10.1)
// ---------------------------------------------------------------------------

/**
 * Where to run to meet a loose ball. (Owner: "bot calculate the ball posi and
 * go there".)
 *
 * Fixed-point iteration on "where will it be when I get there" — unchanged in
 * shape. What changed is the ball model inside it.
 *
 * ── WHY THE OLD ONE PUT HIM BEHIND THE BALL ─────────────────────────────────
 *
 * It extrapolated `ball.v · t · AI_BALL_PREDICT_DAMP` — a straight line at 72 %
 * of the CURRENT speed, forever. A grounded ball in this sim does nothing of the
 * kind: it is skidding, it loses 5-12 m/s², and it STOPS. MEASURED against a
 * stepped ball (ai/ball-travel.test.ts):
 *
 *     v0        real distance in 1.5 s      old model said
 *     6 m/s            3.71 m                  6.48 m   (+75 %)
 *    10 m/s            7.28 m                 10.80 m   (+48 %)
 *    18 m/s           17.06 m                 19.44 m   (+14 %)
 *
 * So on every slow or medium loose ball the chaser was sent to a point the ball
 * never reached, arrived, and found the ball behind him — and because Layer 3
 * picks the chaser with the SAME model (team-ai.ts interceptTicks), it often
 * picked the wrong man too. MEASURED over 3 seeds x 3 sim-min: chasers won only
 * 31 % of loose balls and arrived within 1.2 m of the ball 36 % of the time.
 *
 * ── WHAT IS STILL DELIBERATELY WRONG ────────────────────────────────────────
 *
 * ball-travel.ts models neither Magnus nor the drag crisis, so a curling or
 * dipping ball still wrong-foots the AI exactly as goalkeeper.ts's own
 * approximation lets a bent shot beat the keeper (§4.5) — §10.2's point stands.
 * On top of that, a ball still IN THE AIR at the meeting point keeps a slice of
 * the old damped guess (AI_AIR_INTERCEPT_TRUST): a man reads a rolling ball
 * almost perfectly and an aerial one much less well, and this is where that
 * asymmetry lives. The flat, boring, on-the-grass case — the one a real player
 * gets right, and the one that is 90 % of loose balls — is now solved properly.
 */
/**
 * When will this ball have slowed to `target` m/s? Airborne time counts as-is
 * (the horizontal speed barely changes in the air), then the skid law takes over.
 * Returns 0 if it is already slow enough.
 */
function timeForBallToSlowTo(ball: WorldState['ball'], target: number): number {
  const hSpeed = Math.sqrt(ball.vx * ball.vx + ball.vz * ball.vz);
  if (hSpeed <= target) return 0;
  let v = hSpeed;
  let t = 0;
  while (t < BALL_TRAVEL_MAX_HORIZON_S && v > target) {
    v -= groundDecel(v) * BALL_TRAVEL_SUBSTEP_S;
    t += BALL_TRAVEL_SUBSTEP_S;
  }
  return t;
}

/**
 * Could an OPPONENT get to this ball inside `t` seconds? If so we are in a race
 * and must not stand off waiting for the ball to slow down.
 *
 * Uses the same solve, and only opponents the player can SEE (§10.2): standing
 * off because of a defender you have not looked at would be the AI acting on
 * information it does not have.
 */
function opponentBeatsUsTo(b: AiBrain, id: number, t: number): boolean {
  const p = b.world.players;
  const ball = b.world.ball;
  const team = p.team[id]!;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === team) continue;
    if (!canSee(b.world, id, p.px[i]!, p.pz[i]!)) continue;
    solveIntercept(
      p.px[i]!, p.pz[i]!,
      ball.px, ball.py, ball.pz,
      ball.vx, ball.vy, ball.vz,
      MAX_SPEED_RUN, AI_INTERCEPT_ITERATIONS, oppScratch,
    );
    if (oppScratch.t < t) return true;
  }
  return false;
}

function interceptPoint(b: AiBrain, id: number, out: SpacePoint): void {
  const p = b.world.players;
  const ball = b.world.ball;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  // The player's own realistic closing speed — not a boosted one (§10.2).
  const speed = MAX_SPEED_RUN;

  solveIntercept(
    px, pz,
    ball.px, ball.py, ball.pz,
    ball.vx, ball.vy, ball.vz,
    speed, AI_INTERCEPT_ITERATIONS, interceptScratch,
  );

  // ── TAKE IT WHERE YOU CAN CONTROL IT ────────────────────────────────────────
  //
  // The earliest intercept is not the best one. updatePossession (ball.ts) gives
  // a player the ball only when the ball-vs-player CLOSING speed is under
  // POSSESSION_CAPTURE_REL_SPEED (5 m/s) — so a receiver who sprints at 6 m/s
  // into a pass still doing 9 m/s is closing at 15 m/s and cannot possibly
  // control it. He deflects it and the 50/50 stays live.
  //
  // THIS IS WHAT WAS ACTUALLY HAPPENING. The pass power itself is fine: MEASURED
  // over the whole distance band, the sim's own fit lands a pass at the receiving
  // MAN at 1-3 m/s. But the measured arrival speed in real play was a median of
  // 12.3 m/s, because the chaser ran INTO the ball and met it while it was still
  // travelling. That single mismatch is most of "ball loose 43-63 % of in-play
  // time" and, downstream, most of the balls that ran out of play.
  //
  // So: if the earliest meeting point is a HOT ball, look further down the ball's
  // own path for the point where it has slowed to something trappable, and go
  // there instead — as long as (a) he can actually get there, and (b) nobody on
  // the other side can reach the ball sooner. If it IS a race, he attacks the
  // earliest point, because a contested ball you wait for is a ball you lose.
  if (interceptScratch.ballSpeed > POSSESSION_CAPTURE_REL_SPEED && !interceptScratch.ballAtRest) {
    const trapT = timeForBallToSlowTo(ball, AI_PASS_ARRIVAL_IDEAL);
    if (trapT > interceptScratch.t) {
      predictBall(ball.px, ball.py, ball.pz, ball.vx, ball.vy, ball.vz, trapT, trapScratch);
      const reachable = distXZ(px, pz, trapScratch.x, trapScratch.z) <= speed * trapT;
      if (reachable && !opponentBeatsUsTo(b, id, trapT)) {
        interceptScratch.x = trapScratch.x;
        interceptScratch.z = trapScratch.z;
        interceptScratch.t = trapT;
        interceptScratch.ballSpeed = trapScratch.speed;
      }
    }
  }

  if (ball.py > BALL_RADIUS + 0.05) {
    // Airborne: blend back toward the old damped guess so a lofted ball is read
    // imperfectly. A grounded ball gets the full solution.
    const dampedX = ball.px + ball.vx * interceptScratch.t * AI_BALL_PREDICT_DAMP;
    const dampedZ = ball.pz + ball.vz * interceptScratch.t * AI_BALL_PREDICT_DAMP;
    out.x = dampedX + (interceptScratch.x - dampedX) * AI_AIR_INTERCEPT_TRUST;
    out.z = dampedZ + (interceptScratch.z - dampedZ) * AI_AIR_INTERCEPT_TRUST;
  } else {
    out.x = interceptScratch.x;
    out.z = interceptScratch.z;
  }
  out.control = interceptScratch.t;
}

const looseBall: BtNode = (b, id) => {
  const p = b.world.players;
  const ball = b.world.ball;

  const ballDist = distXZ(p.px[id]!, p.pz[id]!, ball.px, ball.pz);

  // ── WHO ACTUALLY GOES FOR IT (§7) ───────────────────────────────────────────
  //
  // Two people, and BOTH clauses are the fix.
  //
  // (1) THE MAN LAYER 3 NAMED, unconditionally. He used to be allowed to refuse
  //     the job on distance — `... && ballDist < AI_CONTEST_MAX_DISTANCE` — and
  //     then fell through to the shape branch below. That is a contradiction:
  //     assignLoose already compared every teammate's intercept and this man won,
  //     so if he will not go, nobody will — and the commitment hysteresis means
  //     nobody is nominated in his place either. MEASURED
  //     (ai/ball-engagement.test.ts, 6 seeds x 3 sim-min): the assigned chaser
  //     stood off on 14.0 % / 17.3 % of all loose-ball ticks, on top of the
  //     4.1-4.8 % where no chaser existed at all.
  //
  // (2) ANYBODY WITH IT INSIDE AI_LOOSE_CLAIM_RADIUS. A nominated chaser does not
  //     make the ball invisible to the man standing on it; the old node asked only
  //     "am I the chaser", so a HoldShape player with the ball at his feet turned
  //     round and jogged back to his formation anchor. MEASURED: the NEAREST man
  //     of each team was not closing on a loose ball within 8 m of him 34.8 % /
  //     34.3 % of the time.
  //
  // This is NOT a relaxation of the anti-swarm rule and it does not touch
  // `assignment`, so ai/loose-ball-organisation.test.ts's "exactly one Chase" holds
  // by construction. The claim radius is short enough that it can only engage a
  // player already inside the 3.5 m crowding bound that same test asserts — it
  // pulls nobody in from outside, it just stops a man who is already there from
  // turning his back and walking away.
  // ⚠ THE OPPORTUNIST ONLY CLAIMS A BALL OUR OWN TEAM DID NOT JUST PLAY, and that
  // restriction was MEASURED, not assumed. Without it the clause also fired on our
  // OWN pass in flight: a second man standing near the ball's path converged on it
  // and got a foot to it before the intended receiver, so the pass never reached
  // the man it was aimed at. Pass completion fell 54.1 % -> 40.4 % over 6 seeds
  // (2.9 sigma on n=198 — a real regression, not noise), and it fought
  // team-ai.ts applyPassIntent, which had just been built to send the INTENDED
  // receiver. Restricting the clause to a ball last touched by the OPPOSITION
  // leaves 48.4 % (1.2 sigma vs base — no longer a significant regression) and
  // keeps what the clause is for: nobody ignores a loose ball at his feet.
  const contest =
    b.assignment[id] === Assignment.Chase ||
    (ballDist < AI_LOOSE_CLAIM_RADIUS && b.world.ball.lastTouchedTeam !== b.team);

  if (contest) {
    interceptPoint(b, id, space);
    seek(b, id, space.x, space.z);
    // He faces the ball, which costs him the pitch: a man collecting a pass from
    // behind him turns his back on the game, and because collectPassOptions gates
    // on the facing cone his first decision as the new carrier is blind forward.
    //
    // RECEIVING ON THE HALF-TURN — aiming at the bisector of "at the ball" and
    // "upfield", gated on being first to it — WAS BUILT AND MEASURED, and shelved.
    // It did improve what it targeted (forward teammates outside the cone
    // 39.5 % → 29.1 %, usable as a pass 21.5 % → 29.5 %, box entries 45 → 54) but
    // it cost 10 % of LIVE PLAY: over 4 seeds live ticks fell 42,032 → 37,872, and
    // on the academy 11v11 soak balls out of play went 1 → 6 and the match stopped
    // reaching full time inside its tick budget.
    //
    // The likely mechanism, and what to fix FIRST if you pick this up:
    // tryIntercept (player-controller.ts, owned elsewhere) gates trapping a moving
    // ball on INTERCEPT_AWARENESS_CONE_DEG. A half-turned receiver sits near the
    // edge of that cone, so firm passes ran past him and out. The half-turn is
    // worth having, but only together with a receiver who can still trap what he
    // is half-turned away from — that is a player-controller change, not an ai one.
    faceSeek(b, id);
    setSprint(b, id, true);
    // BOOST THE RACE HE IS LOSING (owner: "let the bots use the boost", and
    // "each bot has its own smart brain so it can take decisions").
    //
    // `interceptPoint` has just solved how long HE needs at running speed;
    // `opponentBeatsUsTo` asks whether a visible opponent gets there first. A
    // yes is the one moment where the stamina is unarguably worth spending -
    // at running speed this ball is theirs, and a chase he arrives second to
    // is worth nothing. A no means he already wins it, and burning the bar to
    // arrive early at a ball nobody is contesting is how a bot ends the half
    // walking. Tier 2 only when he is close enough that the extra gear still
    // lands inside the race.
    // (`interceptPoint` returns the solved arrival time in `control` — the
    // field is the influence map's control value everywhere else, and this one
    // caller overloads it.)
    const raceT = space.control;
    if (opponentBeatsUsTo(b, id, raceT)) {
      setBoost(b, id, raceT < AI_BOOST_TIER2_RACE_S ? 2 : 1);
    }
    // NOTE: InputFlags.FirstTouch is deliberately NOT set. It reads like the
    // flag for "take a controlling touch", but no sim code consumes it — the
    // only reference is rules/fsm.ts's restart-illegal mask, and tryIntercept()
    // keys its retention off the firstTouch ATTRIBUTE, not the flag. Setting it
    // would be a comment describing behaviour that does not exist. See report.

    // ⚠ THE MISSING HEADER (owner: "make sure players can hit the ball with
    // head knee chest" -> confirmed via follow-up: "AI should head it more
    // often"). ball.ts's findStriker requires strikeRequested() — a Pass/
    // Shoot bit on the input — for ANY loose-ball strike, headers included
    // ("this is what lets volleys, headers and first-time clearances
    // happen"). This contest branch, the ONLY AI path that can ever reach a
    // ball still above header height (decideOnBall's HasBall gate cannot:
    // possession itself is capped at POSSESSION_MAX_HEIGHT, 1.0m — "a header
    // is not control"), only ever set MOVEMENT flags. The AI chased every
    // header to its landing spot and never once asked to play it — a human
    // holding Shoot while running onto the same ball already could.
    //
    // Reusing emitShot rather than inventing a new emit path: buildHeader
    // (ball.ts) reads input.aimYaw/liftBias exactly like a footed shot, and
    // ball.ts's own dispatcher fires the header unconditionally once airborne
    // — it does not care whether decKick resolves to Shoot or Pass. Aim at
    // goal when in range (a real striker meets a cross with a shot, not a
    // header-then-think), otherwise a lofted ball forward — the same
    // fallback direction progressForwardClearance uses for "get out of
    // trouble", and headers under pressure being looped clear rather than
    // flat is the realistic picture anyway. strikeWouldLand's own reach/
    // facing/height gates (mirrored from findStriker) decide the actual
    // timing, same as every other emitShot call site — emitting early while
    // still closing is harmless, the strike buffer just holds it.
    if (ball.py > HEADER_CONTACT_HEIGHT) {
      const half = halfLengthOf(b);
      const goalX = b.attackSign * half;
      const aimZ = shotAimZ(b, id);
      const distGoal = distXZ(p.px[id]!, p.pz[id]!, goalX, aimZ);
      if (distGoal <= b.params.shootRange) {
        emitShot(b, id, goalX, aimZ, distGoal);
      } else {
        emitShot(b, id, p.px[id]! + b.attackSign * AI_HEADER_CLEARANCE_DIST, 0, AI_HEADER_CLEARANCE_DIST);
      }
    }
    return Bt.Success;
  }

  // Not my ball to chase. Layer 3 already picked someone; converging on it too
  // is how a defence gets pulled apart by one bounce.
  seek(b, id, b.shapeX[id]!, b.shapeZ[id]!);
  faceSeek(b, id);
  sprintIfFar(b, id);
  return Bt.Success;
};

// ---------------------------------------------------------------------------
// Opponent has the ball: press / mark / cover / hold the line (§10.1)
// ---------------------------------------------------------------------------

/**
 * DUEL STATES (Phase-1 Commit 7). A defender near the man with the ball is in
 * a duel whatever Layer 3 has him labelled as.
 *
 *   APPROACH  close the space, get goal-side
 *   CONTAIN   jockey, deny the dangerous route, wait for the ball to come free
 *   ENGAGE    the opportunity is rising — close the last of it
 *   CHALLENGE committed; the action is running and is not reconsidered
 *   RECOVER   the challenge failed — no re-tackle, get balance and goal-side
 */
const enum DuelState { Approach = 0, Contain = 1, Engage = 2, Challenge = 3, Recover = 4 }

/**
 * WHERE DID THAT CHALLENGE COME FROM? (review gate)
 *
 * The probe's 13/24 ball-side wins were real tackles, but they proved nothing
 * about `runDuel` — with the state machine stuck below ENGAGE they were all
 * coming from the legacy `Press` branch. Counting the source is the only way
 * to show the new controller is actually generating challenges. Diagnostic
 * only: a plain module counter, never read by the sim, reset by tests.
 */
// ⚠ BROWSER-SAFE, deliberately. This module is bundled into the CLIENT
// (@fg/shared → vite), where `process` does not exist and a bare
// `process.env` at module top level throws at import — the whole game failed
// to boot with "process is not defined". The guard keeps the diagnostic
// reachable from a Node test run and inert everywhere else.
const SAMPLE_DUEL_SCORES =
  typeof process !== 'undefined' && process.env?.DUEL_SAMPLES === '1';
export const tackleSource = { duel: 0, legacyPress: 0 };
export function resetTackleSource(): void {
  tackleSource.duel = 0;
  tackleSource.legacyPress = 0;
}

/** Opportunity-score samples by duel state, for re-deriving the thresholds
 *  now that the timer is no longer being reset under them. */
export const duelScoreSamples: { contain: number[]; engage: number[] } = { contain: [], engage: [] };
export function resetDuelScoreSamples(): void {
  duelScoreSamples.contain.length = 0;
  duelScoreSamples.engage.length = 0;
}

/**
 * ENGAGE EPISODE TELEMETRY (Commit B diagnosis, 2026-08-16).
 *
 * The question Commit B has to answer before it changes anything: "why does
 * ENGAGE usually fail to earn ball-first geometry before it exits?" One record
 * per ENGAGE episode, sampled EVERY TICK (not at the 10 Hz decision rate) by
 * `sampleEngageEpisode`, closed with exactly one explicit reason. Diagnostic
 * only — a module array, gated on the same DUEL_SAMPLES flag as the score
 * samples, never read by the sim, reset by tests. `OTHER` becoming common
 * means this instrument is incomplete, not that the machine is.
 */
export const enum EngageExit {
  ChallengeCommitted = 'CHALLENGE_COMMITTED',
  /** The step itself won it: possession passed to the engaging defender with
   *  no tackle action — the sim's possession/contest awarded him the ball. */
  BallWon = 'BALL_WON',
  ScoreDropped = 'ENGAGE_SCORE_DROPPED',
  BallNoLongerExposed = 'BALL_NO_LONGER_EXPOSED',
  PossessionChanged = 'POSSESSION_CHANGED',
  TargetInvalid = 'TARGET_INVALID',
  LeftDuelRange = 'LEFT_DUEL_RANGE',
  HandedToTeammate = 'HANDED_TO_TEAMMATE',
  ActionLocked = 'ACTION_LOCKED',
  CarrierEscaped = 'CARRIER_ESCAPED',
  DefenderDown = 'DEFENDER_DOWN',
  TeamStateChanged = 'TEAM_STATE_CHANGED',
  Other = 'OTHER',
}
export interface EngageEpisode {
  playerId: number; carrier: number; assignment: number;
  startTick: number; endTick: number; durationMs: number; samples: number;
  exitReason: string;
  startEngageScore: number; minEngageScore: number; maxEngageScore: number; endEngageScore: number;
  startBallFirstMargin: number; bestBallFirstMargin: number; endBallFirstMargin: number; marginDelta: number;
  startDefenderToBall: number; bestDefenderToBall: number; endDefenderToBall: number;
  startDefenderToTarget: number; bestDefenderToTarget: number; endDefenderToTarget: number;
  startCarrierToBall: number; maxCarrierToBall: number;
  startDefenderSpeed: number; maxDefenderSpeed: number; meanDefenderSpeed: number;
  startFacingError: number; bestFacingError: number;
  ballSpeedAtStart: number; carrierSpeedAtStart: number;
  startExposure: number; maxExposure: number; endExposure: number;
  challengeBecameValid: boolean; timeToFirstValidChallengeMs: number;
  /** ticks in which `legal` (ball-first + commit margin) held, regardless of range */
  legalTicks: number;
  /** ticks in which the defender's move intent magnitude was under 0.5 (the arrive ease-in) */
  slowIntentTicks: number;
  /** Commit B1: decisions on which the score wanted out but the continuation held, and grace ticks used. */
  continuationGrants: number; continuationTicks: number;
  /** Commit B1: why the continuation was denied at the exit (or '' if the score never wanted out). */
  continuationDenied: string;
}
/**
 * CONTINUATION TELEMETRY (Commit B1, §14 of the brief) — plain counters, so the
 * continuation is auditable: how often it was granted, why it was denied, and
 * what a granted continuation turned into.
 */
export const engageContinuation = {
  granted: 0, denied: 0, toChallenge: 0, toAbort: 0, toBallWon: 0,
  deniedExposureLow: 0, deniedNotClosing: 0, deniedMarginNotImproving: 0, deniedCapExpired: 0,
};
export function resetEngageContinuation(): void {
  for (const k of Object.keys(engageContinuation) as Array<keyof typeof engageContinuation>) engageContinuation[k] = 0;
}
export const engageEpisodes: EngageEpisode[] = [];
/**
 * Abandon any episode still open WITHOUT recording it — for a caller that
 * simulates several independent worlds in one process (a fresh `world.tick`
 * per world) and accumulates episodes across all of them. `openEngage` is
 * module-level, so an episode left open when one world's run ends would
 * otherwise carry its `startTick` into the next world's tick space and close
 * with a nonsense (large negative) `durationMs` the moment that player id's
 * slot is touched again. Order-statistics (median/p90/max) are robust to that
 * one outlier; a mean is not — this is what fixes the mean, not just hides it.
 */
export function abandonOpenEngageEpisodes(): void {
  for (let i = 0; i < MAX_PLAYERS; i++) openEngage[i] = null;
}
export function resetEngageEpisodes(): void {
  engageEpisodes.length = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) openEngage[i] = null;
}
const openEngage: Array<EngageEpisode | null> = new Array(MAX_PLAYERS).fill(null);
const engageTmp = { x: 0, z: 0 };

function engageMeasure(b: AiBrain, id: number, carrier: number): {
  margin: number; dBall: number; dTarget: number; dCarrierBall: number; speed: number;
  facingErrDeg: number; ballSpeed: number; carrierSpeed: number; exposure: number; legal: boolean;
} {
  const w = b.world; const p = w.players; const ball = w.ball;
  engageStepPoint(w, id, engageTmp);
  const dx = ball.px - p.px[id]!, dz = ball.pz - p.pz[id]!;
  const want = datan2(dz, dx);
  return {
    margin: ballFirstMargin(w, id, carrier),
    dBall: Math.hypot(dx, dz),
    dTarget: distXZ(p.px[id]!, p.pz[id]!, engageTmp.x, engageTmp.z),
    dCarrierBall: distXZ(p.px[carrier]!, p.pz[carrier]!, ball.px, ball.pz),
    speed: Math.hypot(p.vx[id]!, p.vz[id]!),
    facingErrDeg: Math.abs(angleDelta(p.yaw[id]!, want)) * 180 / Math.PI,
    ballSpeed: Math.hypot(ball.vx, ball.vz),
    carrierSpeed: Math.hypot(p.vx[carrier]!, p.vz[carrier]!),
    exposure: ballExposure(w, carrier),
    legal: reachesBallBeforeMan(w, id, carrier, AI_TACKLE_COMMIT_MARGIN),
  };
}

function openEngageEpisode(b: AiBrain, id: number, carrier: number, engage: number): void {
  if (!SAMPLE_DUEL_SCORES) return;
  const m = engageMeasure(b, id, carrier);
  openEngage[id] = {
    playerId: id, carrier, assignment: b.assignment[id]!,
    startTick: b.world.tick, endTick: b.world.tick, durationMs: 0, samples: 0,
    exitReason: EngageExit.Other,
    startEngageScore: engage, minEngageScore: engage, maxEngageScore: engage, endEngageScore: engage,
    startBallFirstMargin: m.margin, bestBallFirstMargin: m.margin, endBallFirstMargin: m.margin, marginDelta: 0,
    startDefenderToBall: m.dBall, bestDefenderToBall: m.dBall, endDefenderToBall: m.dBall,
    startDefenderToTarget: m.dTarget, bestDefenderToTarget: m.dTarget, endDefenderToTarget: m.dTarget,
    startCarrierToBall: m.dCarrierBall, maxCarrierToBall: m.dCarrierBall,
    startDefenderSpeed: m.speed, maxDefenderSpeed: m.speed, meanDefenderSpeed: 0,
    startFacingError: m.facingErrDeg, bestFacingError: m.facingErrDeg,
    ballSpeedAtStart: m.ballSpeed, carrierSpeedAtStart: m.carrierSpeed,
    startExposure: m.exposure, maxExposure: m.exposure, endExposure: m.exposure,
    challengeBecameValid: false, timeToFirstValidChallengeMs: -1,
    legalTicks: 0, slowIntentTicks: 0,
    continuationGrants: 0, continuationTicks: 0, continuationDenied: '',
  };
}

/** Called at a decision tick with the score in hand (the 10 Hz view). */
function noteEngageScore(id: number, engage: number): void {
  const e = openEngage[id];
  if (!e) return;
  e.endEngageScore = engage;
  if (engage < e.minEngageScore) e.minEngageScore = engage;
  if (engage > e.maxEngageScore) e.maxEngageScore = engage;
}

/** Diagnostic: stamp the reason on the open episode; the per-tick sampler closes it. */
function markEngageExit(id: number, reason: string): void {
  const e = openEngage[id];
  if (e && e.exitReason === EngageExit.Other) e.exitReason = reason;
}

function closeEngageEpisode(b: AiBrain, id: number, reason: string): void {
  const e = openEngage[id];
  if (!e) return;
  e.exitReason = reason;
  e.endTick = b.world.tick;
  e.durationMs = ((e.endTick - e.startTick) / SIMULATION_TICK_RATE) * 1000;
  e.marginDelta = e.bestBallFirstMargin - e.startBallFirstMargin;
  if (e.samples > 0) e.meanDefenderSpeed /= e.samples;
  engageEpisodes.push(e);
  openEngage[id] = null;
}

/**
 * Per-tick sampler + exit detector. Called from TeamAI.produceInputs for every
 * slot it owns, AFTER the decision and the steer for that tick, so it sees the
 * 60 Hz truth rather than the 10 Hz decision.
 */
export function sampleEngageEpisode(b: AiBrain, id: number, moveMag: number): void {
  if (!SAMPLE_DUEL_SCORES) return;
  const e = openEngage[id];
  if (!e) return;
  const w = b.world; const p = w.players;
  const inEngage = b.inDuel[id] === 1 && (b.duelState[id]! as DuelState) === DuelState.Engage;
  if (!inEngage) {
    // The transition site set a reason if it knew one; otherwise infer.
    if (e.exitReason === EngageExit.Other) {
      const carrier = w.ball.possessedBy;
      if (p.knockdownTimer[id]! > 0) e.exitReason = EngageExit.DefenderDown;
      else if (carrier === id) { e.exitReason = EngageExit.BallWon; if (e.continuationGrants > 0) engageContinuation.toBallWon++; }
      else if (carrier !== e.carrier) e.exitReason = EngageExit.PossessionChanged;
      else if (p.knockdownTimer[carrier]! > 0 || p.sentOff[carrier] === 1) e.exitReason = EngageExit.TargetInvalid;
      else if (b.inDuel[id] !== 1) e.exitReason = EngageExit.LeftDuelRange;
      else e.exitReason = EngageExit.TeamStateChanged;
    }
    closeEngageEpisode(b, id, e.exitReason);
    return;
  }
  // Still engaging — but is it still the same duel? A carrier change with the
  // machine left in ENGAGE (the ball went loose and the duel node was skipped)
  // is a POSSESSION_CHANGED exit, and the stale ENGAGE is itself a finding.
  const carrier = w.ball.possessedBy;
  if (carrier !== e.carrier) {
    if (carrier === id && e.continuationGrants > 0) engageContinuation.toBallWon++;
    closeEngageEpisode(b, id, carrier === id ? EngageExit.BallWon : EngageExit.PossessionChanged);
    return;
  }
  const m = engageMeasure(b, id, carrier);
  e.samples++;
  e.endBallFirstMargin = m.margin; if (m.margin > e.bestBallFirstMargin) e.bestBallFirstMargin = m.margin;
  e.endDefenderToBall = m.dBall; if (m.dBall < e.bestDefenderToBall) e.bestDefenderToBall = m.dBall;
  e.endDefenderToTarget = m.dTarget; if (m.dTarget < e.bestDefenderToTarget) e.bestDefenderToTarget = m.dTarget;
  if (m.dCarrierBall > e.maxCarrierToBall) e.maxCarrierToBall = m.dCarrierBall;
  if (m.speed > e.maxDefenderSpeed) e.maxDefenderSpeed = m.speed;
  e.meanDefenderSpeed += m.speed;
  if (m.facingErrDeg < e.bestFacingError) e.bestFacingError = m.facingErrDeg;
  e.endExposure = m.exposure; if (m.exposure > e.maxExposure) e.maxExposure = m.exposure;
  if (m.legal) e.legalTicks++;
  if (moveMag < 0.5) e.slowIntentTicks++;
  const dist = distXZ(p.px[id]!, p.pz[id]!, p.px[carrier]!, p.pz[carrier]!);
  if (m.legal && dist <= SLIDE_TACKLE_RANGE && !e.challengeBecameValid) {
    e.challengeBecameValid = true;
    e.timeToFirstValidChallengeMs = ((w.tick - e.startTick) / SIMULATION_TICK_RATE) * 1000;
  }
}

/** Seconds spent in the current duel state. */
function duelHeldS(b: AiBrain, id: number): number {
  return b.duelTicks[id]! / SIMULATION_TICK_RATE;
}

/**
 * Leaving a duel — run EXACTLY ONCE on the in→out transition, never per tick.
 *
 * The per-tick stamp was the lifecycle bug (it zeroed duelTicks every frame and
 * made ENGAGE unreachable); doing nothing at all was the overcorrection, and it
 * left CHALLENGE/RECOVER stored for ~18 s after the duel ended, which made the
 * durations meaningless and let RECOVER's anti-spam window expire while the
 * defender was somewhere else entirely.
 *
 * ⚠ TACTICAL STATE ONLY. The physical consequences of having committed — the
 * tackle action running to its contact frame, and the cooldown that stops him
 * throwing another boot at it — are ACTION state and deliberately survive this.
 * `tackleCooldown` is that lock and is not touched here, so a defender who
 * commits and then loses the duel still cannot immediately re-tackle.
 */
function exitDuel(b: AiBrain, id: number, reason: string = EngageExit.LeftDuelRange): void {
  if ((b.duelState[id]! as DuelState) === DuelState.Engage) markEngageExit(id, reason);
  b.inDuel[id] = 0;
  b.duelState[id] = DuelState.Approach;
  b.duelTicks[id] = 0;
  b.prevExposure[id] = 0;
}

/**
 * THE DUEL ENDS WHEN THE BALL CHANGES HANDS (Commit B0.5, lifecycle).
 *
 * `runDuel` is only reached while an OPPONENT carries, so when the ball goes
 * loose or to a team-mate the machine was simply never consulted again: it sat
 * in whatever state it held — ENGAGE, most often — with `inDuel` still set and
 * `duelTicks` still counting. Found by the Commit-B instrument: episodes closed
 * POSSESSION_CHANGED with the state stale, and the next duel then RESUMED in
 * ENGAGE with a minutes-old timer that satisfied every minimum hold at once.
 * Called from produceInputs every tick; it is the same in→out transition
 * `exitDuel` already owns, reached from the one place that always runs. This
 * is tactical state only — tackle/action recovery is untouched.
 */
export function leaveDuelIfPossessionChanged(b: AiBrain, id: number): void {
  if (b.inDuel[id] !== 1) return;
  const c = b.world.ball.possessedBy;
  if (c < 0 || b.world.players.team[c]! === b.team) {
    exitDuel(b, id, c === id ? EngageExit.BallWon : EngageExit.PossessionChanged);
  }
}

function setDuel(b: AiBrain, id: number, next: DuelState): void {
  if (b.duelState[id]! === next) return;
  b.duelState[id] = next;
  b.duelTicks[id] = 0;
}

/**
 * How close should he sit while containing? (Phase-1 Commit 8)
 *
 * A single constant gap is the same defender against a sprinter in midfield
 * and against a man lining up a shot on the edge of the box, and those want
 * opposite things: give the sprinter room so he cannot simply knock it past
 * you, and get tight to the shooter because the shot is the threat.
 */
function containDistance(b: AiBrain, carrier: number): number {
  const p = b.world.players;
  const speed = Math.hypot(p.vx[carrier]!, p.vz[carrier]!);
  const fast = clamp01(speed / MAX_SPEED_SPRINT);
  let d = CONTAIN_DIST_MIN_M
    + (CONTAIN_DIST_MAX_M - CONTAIN_DIST_MIN_M) * fast * CONTAIN_SPEED_GIVE;
  // Nearer our goal, tighten: you cannot show a man a shot.
  const goalX = -b.attackSign * halfLengthOf(b);
  const toGoal = distXZ(p.px[carrier]!, p.pz[carrier]!, goalX, 0);
  const threat = 1 - clamp01(toGoal / CONTAIN_SHOOTING_RANGE_M);
  d *= 1 - threat * CONTAIN_SHOOTING_TIGHTEN;
  return d;
}

/**
 * Run the duel for a defender who is near the carrier. Returns true when it
 * took over his movement/action for this tick.
 *
 * ⚠ THE HARD GATE IS STILL THE HARD GATE. `reachesBallBeforeMan` decides
 * whether a challenge is physically legal and nothing here can overrule it;
 * `scoreTackleOpportunity` only ranks challenges that already passed.
 */
function runDuel(b: AiBrain, id: number, carrier: number): boolean {
  const w = b.world;
  const p = w.players;
  const dist = distXZ(p.px[id]!, p.pz[id]!, p.px[carrier]!, p.pz[carrier]!);
  if (dist > STEAL_ENGAGEMENT_RANGE) {
    // ⚠ LEAVE THE STATE AND THE TIMER ALONE. This used to stamp APPROACH and,
    // through setDuel, zero duelTicks — every tick, for every defender not
    // currently in a duel. MEASURED: 22,196 transitions in 5,400 ticks, 99.9 %
    // of them under 50 ms, CONTAIN's median duration 0 ms, and therefore
    // `held >= DUEL_MIN_CONTAIN_S` never true and ENGAGE/CHALLENGE/RECOVER
    // never reached ONCE in a full AI match. Bailing out of a duel is not a
    // transition inside one.
    if (b.inDuel[id] === 1) exitDuel(b, id);
    return false;   // too far to be a duel; his ordinary assignment runs
  }

  // ── ONE MAN DUELS, THE REST KEEP THEIR JOBS ────────────────────────────
  //
  // The first cut let EVERY defender inside STEAL_ENGAGEMENT_RANGE (7 m) take
  // over his own decision and jockey the carrier. In an 11v11 that is three or
  // four men abandoning their assignments to crowd one player — the swarm the
  // whole layered-assignment system exists to prevent. MEASURED: live play
  // fell to 2750 ticks of 10800 (25 %) against a 30 % floor, because a
  // defence with no shape concedes and restarts constantly.
  //
  // So the duel belongs to the man who is actually in it: the nearest
  // team-mate to the carrier, plus anyone already inside tackling distance
  // (he is in the duel whether or not he is nearest, and walking him out of it
  // to go stand on a zone would look absurd). Everyone else keeps their
  // assignment and holds the shape — which is what covers the duel.
  if (dist > STEAL_CHALLENGE_RANGE) {
    for (let i = 0; i < MAX_PLAYERS; i++) {
      if (i === id || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
      if (p.team[i]! !== b.team || p.knockdownTimer[i]! > 0) continue;
      if (distXZ(p.px[i]!, p.pz[i]!, p.px[carrier]!, p.pz[carrier]!) < dist) {
        if (b.inDuel[id] === 1) exitDuel(b, id, EngageExit.HandedToTeammate);
        return false; // somebody else's duel — same reason, do not stamp state
      }
    }
  }

  b.inDuel[id] = 1;
  const state = b.duelState[id]! as DuelState;
  const held = duelHeldS(b, id);

  // RECOVER first — it outranks everything, because the whole point is that a
  // missed challenge costs you something. Without it a defender who whiffs
  // simply throws another boot at the ball on the next tick, which is the
  // "frame-perfect re-tackle" the owner called out by name.
  if (state === DuelState.Recover) {
    // Anti-spam is the ACTION cooldown; the tactical timer only shapes how long
    // he keeps shepherding rather than defending normally.
    if (held < DUEL_RECOVER_S || b.tackleCooldown[id]! > 0) {
      // Get goal-side and stay on your feet. No challenge is offered at all.
      const goalX = -b.attackSign * halfLengthOf(b);
      let gx = goalX - p.px[carrier]!;
      let gz = 0 - p.pz[carrier]!;
      const gl = Math.hypot(gx, gz) || 1;
      gx /= gl; gz /= gl;
      seek(b, id, p.px[carrier]! + gx * AI_MARK_GOALSIDE_DISTANCE,
                  p.pz[carrier]! + gz * AI_MARK_GOALSIDE_DISTANCE);
      b.decAimYaw[id] = datan2(p.pz[carrier]! - p.pz[id]!, p.px[carrier]! - p.px[id]!);
      return true;
    }
    setDuel(b, id, DuelState.Contain);
  }

  // A committed challenge is not reconsidered. The action owns the window.
  if (state === DuelState.Challenge) {
    if (b.tackleCooldown[id]! > 0) return true;
    setDuel(b, id, DuelState.Recover);   // it resolved; take the recovery
    return true;
  }

  // ── ENGAGE DESIRABILITY vs CHALLENGE LEGALITY — two questions ──────────
  //
  // `opportunity` (with its ballFirstMargin term) is PERMISSION TO CHALLENGE.
  // `engage` is whether an opportunity is developing at all, and it must not
  // carry the margin: CONTAIN puts him goal-side, so the margin is negative by
  // construction and multiplying by it made ENGAGE unreachable — p50 0.000,
  // zero entries in a 90 s match. He is supposed to still be goal-side when he
  // starts his step; the step is what earns him the geometry.
  const exposure = ballExposure(w, carrier);
  const dt = 1 / SIMULATION_TICK_RATE;
  const trend = (exposure - b.prevExposure[id]!) / dt;
  b.prevExposure[id] = exposure;
  const engage = scoreEngageOpportunity(w, id, carrier, exposure, trend);
  const legal = reachesBallBeforeMan(w, id, carrier, AI_TACKLE_COMMIT_MARGIN);
  const want = containDistance(b, carrier);

  // Position: goal-side, at the contain distance, facing him.
  const goalX = -b.attackSign * halfLengthOf(b);
  let gx = goalX - p.px[carrier]!;
  let gz = 0 - p.pz[carrier]!;
  const gl = Math.hypot(gx, gz) || 1;
  gx /= gl; gz /= gl;
  seek(b, id, p.px[carrier]! + gx * want, p.pz[carrier]! + gz * want);
  b.decAimYaw[id] = datan2(p.pz[carrier]! - p.pz[id]!, p.px[carrier]! - p.px[id]!);

  // ── transitions, with ASYMMETRIC thresholds and minimum dwell times ──────
  // Equal thresholds make a defender flicker between contain and engage on
  // noise; the same threshold in both directions IS the flicker.
  if (state === DuelState.Approach) {
    setSprint(b, id, dist > STEAL_CHALLENGE_RANGE);
    // CLOSE THE GAP FOR REAL (owner: "bots... using boost", alongside "make
    // sure the bots slide"). Only past AI_BOOST_DUEL_CLOSE_MIN_M — inside it
    // a plain sprint closes the remaining ground in about a second, and
    // AI_BOOST_SPACE_MIN's own history is the warning against boosting on
    // routine, already-short movement. This is genuine catch-up running: the
    // defender is still outside active-press range and closing on a moving
    // carrier, exactly the recovery-run case CR-8 measured as the real gap
    // on the competitive path (defenders who never reach tackling range at
    // all, not defenders who reach it and decide wrong).
    if (dist > AI_BOOST_DUEL_CLOSE_MIN_M) setBoost(b, id, 1);
    if (dist <= STEAL_CHALLENGE_RANGE && held >= DUEL_MIN_APPROACH_S) {
      setDuel(b, id, DuelState.Contain);
    }
    return true;
  }

  if (state === DuelState.Contain) {
    if (SAMPLE_DUEL_SCORES) duelScoreSamples.contain.push(engage);
    // §4.4's jockey: slower forward, quicker laterally — show him wide, do not
    // dive in. This is the default and a defender should spend most of a duel
    // in it.
    b.decFlags[id] = b.decFlags[id]! | InputFlags.Jockey;
    if (dist > STEAL_CHALLENGE_RANGE + 0.5) { setDuel(b, id, DuelState.Approach); return true; }
    // ⚠ NO `legal` HERE, deliberately. Requiring ball-first access to ENGAGE is
    // requiring the outcome as the entry condition.
    if (engage >= ENGAGE_ENTER && held >= DUEL_MIN_CONTAIN_S) {
      setDuel(b, id, DuelState.Engage);
      // B1: prime the progress memory so the first ENGAGE decision has a rate.
      b.engPrevTick[id] = w.tick;
      b.engPrevMargin[id] = ballFirstMargin(w, id, carrier);
      b.engPrevBallDist[id] = distXZ(p.px[id]!, p.pz[id]!, w.ball.px, w.ball.pz);
      b.engGraceTicks[id] = 0;
      openEngageEpisode(b, id, carrier, engage);
    }
    return true;
  }

  if (SAMPLE_DUEL_SCORES) { duelScoreSamples.engage.push(engage); noteEngageScore(id, engage); }
  // ── ENGAGE — stepping AT THE BALL, not at the man ───────────────────────
  // This is what turns a negative ballFirstMargin positive, and it is the
  // reason ENGAGE exists as a state rather than as an instant.
  engageStepPoint(w, id, stepPt);
  seek(b, id, stepPt.x, stepPt.z);
  // ── IS THE STEP STILL WORKING? (Commit B1) ──────────────────────────────
  // Rates over the decision interval, not the tick: closing on the ball, and
  // ball-first margin improving (or already legal — a flat positive margin
  // while closing is a race being won, not stalled progress). Stateful
  // geometry, deliberately NOT the trend: the trend detected the moment to
  // step and is meant to spike and disappear; it must not also be the reason
  // to keep stepping.
  const marginNow = ballFirstMargin(w, id, carrier);
  const dBallNow = distXZ(p.px[id]!, p.pz[id]!, w.ball.px, w.ball.pz);
  const decTicks = Math.max(1, w.tick - b.engPrevTick[id]!);
  const decDt = decTicks / SIMULATION_TICK_RATE;
  const marginRate = (marginNow - b.engPrevMargin[id]!) / decDt;
  const closeRate = (b.engPrevBallDist[id]! - dBallNow) / decDt;
  b.engPrevTick[id] = w.tick;
  b.engPrevMargin[id] = marginNow;
  b.engPrevBallDist[id] = dBallNow;
  if (engage < ENGAGE_EXIT && held >= DUEL_MIN_ENGAGE_S) {
    // The score has fallen — the trend term is one-shot and it always will,
    // one decision after entry. Grant a SHORT continuation only while the step
    // is demonstrably earning geometry; the moment it stops, or the grace is
    // spent, the opportunity has genuinely vanished. (See ENGAGE_GRACE_MAX_S.)
    const capOk = b.engGraceTicks[id]! < ENGAGE_GRACE_MAX_S * SIMULATION_TICK_RATE;
    const exposureOk = exposure > 0.5;
    const closingOk = closeRate >= ENGAGE_PROGRESS_CLOSE_RATE;
    const marginOk = marginRate >= ENGAGE_PROGRESS_MARGIN_RATE || legal;
    if (exposureOk && closingOk && marginOk && capOk) {
      b.engGraceTicks[id] = b.engGraceTicks[id]! + decTicks;
      if (SAMPLE_DUEL_SCORES) {
        engageContinuation.granted++;
        const ep = openEngage[id];
        if (ep) { ep.continuationGrants++; ep.continuationTicks += decTicks; }
      }
      // fall through: still ENGAGE, still stepping at the ball, still allowed
      // to commit below if the geometry becomes legal.
    } else {
      if (SAMPLE_DUEL_SCORES) {
        engageContinuation.denied++;
        const why = !exposureOk ? 'EXPOSURE_LOW' : !closingOk ? 'NOT_CLOSING'
          : !marginOk ? 'MARGIN_NOT_IMPROVING' : 'CAP_EXPIRED';
        if (why === 'EXPOSURE_LOW') engageContinuation.deniedExposureLow++;
        else if (why === 'NOT_CLOSING') engageContinuation.deniedNotClosing++;
        else if (why === 'MARGIN_NOT_IMPROVING') engageContinuation.deniedMarginNotImproving++;
        else engageContinuation.deniedCapExpired++;
        const ep = openEngage[id];
        if (ep) { ep.continuationDenied = why; if (ep.continuationGrants > 0) engageContinuation.toAbort++; }
      }
      // Diagnostic: WHY did the score drop? Three distinguishable stories.
      markEngageExit(id,
        dist > STEAL_CHALLENGE_RANGE + 0.5 ? EngageExit.CarrierEscaped
        : exposure <= 0.5 ? EngageExit.BallNoLongerExposed
        : EngageExit.ScoreDropped);
      setDuel(b, id, DuelState.Contain);   // the opportunity vanished — abort
      return true;
    }
  }
  // PERMISSION TO CHALLENGE: the hard, unweakened ball-first invariant.
  const facing = dcos(angleDelta(p.yaw[id]!, b.decAimYaw[id]!));
  if (legal && b.tackleCooldown[id]! <= 0 && facing > 0) {
    if (dist <= STANDING_TACKLE_RANGE) {
      b.decAction[id] = ActionIntent.StandingTackle;
      tackleSource.duel++;
      if (SAMPLE_DUEL_SCORES && (openEngage[id]?.continuationGrants ?? 0) > 0) engageContinuation.toChallenge++;
      markEngageExit(id, EngageExit.ChallengeCommitted);
      setDuel(b, id, DuelState.Challenge);
    } else if (
      b.params.slideEnabled &&
      dist > AI_SLIDE_MIN_RANGE && dist <= SLIDE_TACKLE_RANGE &&
      facing > 0.7 && !isLastDefender(b, id) &&
      // A slide is a commitment with a red card at the end of it if you are
      // wrong; rolling keeps the hard tier from being a readable metronome.
      pcg32Float(b.rngFor(id, SALT_SLIDE)) < 0.5
    ) {
      b.decAction[id] = ActionIntent.SlideTackle;
      tackleSource.duel++;
      if (SAMPLE_DUEL_SCORES && (openEngage[id]?.continuationGrants ?? 0) > 0) engageContinuation.toChallenge++;
      markEngageExit(id, EngageExit.ChallengeCommitted);
      setDuel(b, id, DuelState.Challenge);
    }
  } else if (
    // THE LUNGE (CR-8/CR-27/CR-29 — see AI_LUNGE_REQUIRED_MARGIN's doc). Not
    // yet AI-legal, but close enough that the REFEREE would call it clean or
    // fair RIGHT NOW, and this defender is willing to risk it. Standing
    // tackles only — a slide would stack a red-card-risk commitment on top
    // of an already-marginal read, too much at once for a first cut.
    !legal && b.tackleCooldown[id]! <= 0 && facing > 0 &&
    dist <= STANDING_TACKLE_RANGE &&
    reachesBallBeforeMan(w, id, carrier, AI_LUNGE_REQUIRED_MARGIN) &&
    pcg32Float(b.rngFor(id, SALT_LUNGE)) <
      b.params.tackleLungeChance * (isLastDefender(b, id) ? AI_LUNGE_LAST_DEFENDER_MULT : 1)
  ) {
    b.decAction[id] = ActionIntent.StandingTackle;
    tackleSource.duel++;
    if (SAMPLE_DUEL_SCORES && (openEngage[id]?.continuationGrants ?? 0) > 0) engageContinuation.toChallenge++;
    markEngageExit(id, EngageExit.ChallengeCommitted);
    setDuel(b, id, DuelState.Challenge);
  }
  return true;
}

const defend: BtNode = (b, id) => {
  const p = b.world.players;
  const w = b.world;

  // ── THE DUEL COMES FIRST (Phase-1 Commit 7) ────────────────────────────
  //
  // Whoever this man is assigned to mark, cover or screen, if he is standing
  // next to the player with the ball then THAT is his situation and the duel
  // owns his decision. The old code ran the challenge logic only inside the
  // `Press` branch, so a defender on any other assignment could stand 0.25 m
  // from the carrier and never once challenge — measured, `everTackled` false
  // — which before Commit 1 simply meant the ball transferred to him for free.
  {
    const carrier = w.ball.possessedBy;
    if (
      carrier >= 0 && p.team[carrier]! !== b.team &&
      p.knockdownTimer[id]! <= 0 && p.sentOff[id] !== 1 &&
      runDuel(b, id, carrier)
    ) {
      return Bt.Success;
    }
  }
  const carrier = w.ball.possessedBy;
  const a = b.assignment[id]!;

  if (a === Assignment.Press && carrier >= 0 && distXZ(p.px[id]!, p.pz[id]!, p.px[carrier]!, p.pz[carrier]!) <= STEAL_AWARENESS_RANGE) {
    // §2 AWARENESS band: he is aware of the carrier and actively closes. Beyond it
    // (the gate above fails) he falls through to holding his defensive shape.
    const cx = p.px[carrier]!;
    const cz = p.pz[carrier]!;
    const dist = distXZ(p.px[id]!, p.pz[id]!, cx, cz);

    // Approach from the goal side: get between him and the goal, not behind him.
    // The offset shrinks to zero as you arrive, so the last stride is straight
    // at the man rather than a curl that never closes.
    const goalX = -b.attackSign * halfLengthOf(b);
    let gx = goalX - cx;
    let gz = 0 - cz;
    const gl = Math.sqrt(gx * gx + gz * gz);
    if (gl > 1e-6) {
      gx /= gl;
      gz /= gl;
    } else {
      gx = -b.attackSign;
      gz = 0;
    }
    const offset = Math.min(AI_MARK_GOALSIDE_DISTANCE, dist * 0.5);
    seek(b, id, cx + gx * offset, cz + gz * offset);
    b.decAimYaw[id] = datan2(cz - p.pz[id]!, cx - p.px[id]!);
    // §2 CHALLENGE band: sprint to close the space down, then ease into a
    // controlled jockey once inside tackling distance — the last stride is a
    // contain, not a dive-through.
    setSprint(b, id, dist > STEAL_CHALLENGE_RANGE);

    // §2 ENGAGEMENT band: a challenge is only OFFERED once he is close enough to
    // press. Beyond it he just closes and shapes — no lunge from range.
    //
    // ⚠ MOSTLY DEAD CODE NOW, and left in place on purpose. `runDuel` at the
    // top of this node handles every defender inside STEAL_ENGAGEMENT_RANGE of
    // the carrier, which is a superset of what this block ever covered, so in
    // practice this only runs when the carrier is not the man being pressed
    // (a presser closing on a DIFFERENT opponent, e.g. mid-turnover). Deleting
    // it outright is a bigger behavioural change than Commit 7 is scoped to
    // make; it goes when the duel has run through a visual review.
    if (dist <= STEAL_ENGAGEMENT_RANGE && b.tackleCooldown[id]! <= 0) {
      // Facing check: a poke thrown while turned away has ~no chance (§4.4's
      // angle factor) and costs a commit window. Don't throw it.
      const facing = dcos(angleDelta(p.yaw[id]!, b.decAimYaw[id]!));
      // Ball-first check — the single thing that separates a tackle from a foul.
      // resolveTackle() (§4.4) fouls whenever the defender reaches the MAN before
      // the BALL, and a presser sitting goal-side has the carrier's body between
      // himself and the ball, so a tackle there is an AUTOMATIC foul. Measured:
      // committing on range alone gave away 14 fouls in 90s and buried the match
      // in set pieces. So only commit when the ball is genuinely the nearer
      // thing — and read THE SAME PREDICATE the sim will judge this challenge
      // with, never a local mirror of it (sim/tackle-geometry.ts).
      //
      // AI_TACKLE_COMMIT_MARGIN is the LEAD, not a second opinion: this decision
      // is taken ~75-107 ms before the swing's contact frame actually resolves
      // it, and the carrier moves the ball in between. Committing on the
      // referee's own line means committing to a foul.
      const reachable = reachesBallBeforeMan(w, id, carrier, AI_TACKLE_COMMIT_MARGIN);
      if (reachable && dist <= STANDING_TACKLE_RANGE && facing > 0) {
        b.decAction[id] = ActionIntent.StandingTackle;
        tackleSource.legacyPress++;
      } else if (
        reachable &&
        b.params.slideEnabled &&
        dist > AI_SLIDE_MIN_RANGE &&
        dist <= SLIDE_TACKLE_RANGE &&
        facing > 0.7 &&
        !isLastDefender(b, id) &&
        // A slide is a commitment with a red card at the end of it if you are
        // wrong. Rolling for it keeps the hard tier from being a metronome that
        // slides at exactly 2.4m every single time — which is readable, and
        // therefore exploitable, in a way a real defender is not.
        pcg32Float(b.rngFor(id, SALT_SLIDE)) < 0.5
      ) {
        b.decAction[id] = ActionIntent.SlideTackle;
        tackleSource.legacyPress++;
      } else if (dist <= STEAL_CHALLENGE_RANGE) {
        // Contain (CHALLENGE band). §4.4's jockey is slower but shuffles laterally
        // faster — exactly the tool for "don't dive in, show him wide".
        b.decFlags[id] = b.decFlags[id]! | InputFlags.Jockey;
      }
    }
    return Bt.Success;
  }

  if (a === Assignment.Mark) {
    const t = b.markTarget[id]!;
    if (t >= 0 && p.active[t] === 1) {
      // Goal-side and touch-tight.
      const goalX = -b.attackSign * halfLengthOf(b);
      let gx = goalX - p.px[t]!;
      let gz = 0 - p.pz[t]!;
      const gl = Math.sqrt(gx * gx + gz * gz);
      if (gl > 1e-6) {
        gx /= gl;
        gz /= gl;
      } else {
        gx = -b.attackSign;
        gz = 0;
      }
      seek(b, id, p.px[t]! + gx * AI_MARK_GOALSIDE_DISTANCE, p.pz[t]! + gz * AI_MARK_GOALSIDE_DISTANCE);
      // Watch the ball, not the man — you cannot intercept what you cannot see,
      // and tryIntercept() (§4.4) gates on exactly this cone.
      b.decAimYaw[id] = datan2(w.ball.pz - p.pz[id]!, w.ball.px - p.px[id]!);
      sprintIfFar(b, id);
      return Bt.Success;
    }
  }

  if (a === Assignment.Cover) {
    // Drop into the space behind the pressing line: halfway between your anchor
    // and your own goal.
    const goalX = -b.attackSign * halfLengthOf(b);
    seek(b, id, (b.shapeX[id]! + goalX) * 0.5, b.shapeZ[id]! * 0.7);
    b.decAimYaw[id] = datan2(w.ball.pz - p.pz[id]!, w.ball.px - p.px[id]!);
    sprintIfFar(b, id);
    return Bt.Success;
  }

  // Hold the line. The anchor already dropped deep — that is the phase shift's
  // job (§10.1 Layer 3), not this node's.
  seek(b, id, b.shapeX[id]!, b.shapeZ[id]!);
  b.decAimYaw[id] = datan2(w.ball.pz - p.pz[id]!, w.ball.px - p.px[id]!);
  sprintIfFar(b, id);
  return Bt.Success;
};

// The AI's ball-first pre-check used to live here, as a private re-implementation
// of resolveTackle's geometry, under a comment claiming it was "a carbon copy …
// keeping this in lockstep with the controller is the whole point". It was NOT in
// lockstep — it had drifted to a 0.15 m ball advantage while the sim demanded
// 0.49 m, so the AI committed to challenges the sim then whistled, and that alone
// was 137 fouls per 90 minutes. It now calls sim/tackle-geometry.ts, the single
// predicate both sides read, so the drift cannot come back. (§4.4, §5.3)

/** Last man back? Then do not slide — a mistimed one is a red card (§5.3 DOGSO). */
function isLastDefender(b: AiBrain, id: number): boolean {
  const p = b.world.players;
  const team = p.team[id]!;
  const myProj = -b.attackSign * p.px[id]!; // bigger = deeper in our own half
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === id || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    if (b.world.positions[i] === 'GK') continue; // the keeper is not cover
    if (-b.attackSign * p.px[i]! > myProj) return false;
  }
  return true;
}

const holdShape: BtNode = (b, id) => {
  seek(b, id, b.shapeX[id]!, b.shapeZ[id]!);
  faceSeek(b, id);
  return Bt.Success;
};

// ---------------------------------------------------------------------------
// The tree, composed once at module load. (§15.7)
// ---------------------------------------------------------------------------

const ROOT: BtNode = selector([
  sequence([restartStaged, decideRestart]),
  sequence([hasBall, onBall]),
  sequence([teamHasBall, offBallAttack]),
  sequence([ballLoose, looseBall]),
  sequence([oppHasBall, defend]),
  holdShape,
]);

/**
 * Run one player's behaviour tree and leave a fresh decision behind.
 *
 * Called at AI_INDIVIDUAL_TICK_RATE, and NOT called at all while that player's
 * reaction delay is running — which is what a reaction delay is: acting on the
 * decision you made before the thing happened (§10.2).
 */
export function runIndividualAi(b: AiBrain, id: number): void {
  // Persistent flags are rebuilt from scratch every decision; one-shot intents
  // are latched here and consumed by Layer 1 so a decision held across a 10Hz
  // window fires its kick exactly once.
  b.decFlags[id] = 0;
  b.decKick[id] = KickIntent.None;
  b.decAction[id] = ActionIntent.None;
  b.decCharge[id] = 0;
  b.decLift[id] = 0;
  b.decPassTarget[id] = -1;
  ROOT(b, id);
  // Stamp a fresh strike latch so applyDecisionToInput knows how stale it is.
  // A decision REPLACES the previous latch (it was cleared above), so this is
  // also what stops a buffered strike outliving the read of the pitch that
  // produced it: 10Hz re-decides refresh the stamp, and AI_STRIKE_BUFFER_TICKS
  // caps how long one decision may wait for the ball to come to the boot.
  if (b.decKick[id] !== KickIntent.None) b.decKickTick[id] = b.world.tick;
}

/**
 * Turn this tick's decision into the InputCommand a human would have produced.
 * Runs at 60Hz for every AI player (§10.1 Layer 1 rate), and is the ONLY place
 * an AI intent becomes a game input.
 */
export function applyDecisionToInput(
  b: AiBrain,
  id: number,
  moveX: number,
  moveZ: number,
  cmd: InputCommand,
): void {
  cmd.moveX = moveX;
  cmd.moveZ = moveZ;
  cmd.aimYaw = b.decAimYaw[id]!;
  cmd.liftBias = clamp(b.decLift[id]!, -1, 1);
  // Mouse curl is a house control-scheme artifact of a human dragging the mouse
  // during a charge (types.ts): "Zero for AI". Honoured literally.
  cmd.curl = 0;
  cmd.passCharge = 0;
  cmd.shootCharge = 0;
  cmd.skill = 0;
  cmd.tactical = 0;

  let flags = b.decFlags[id]!;

  const kick = b.decKick[id]!;
  if (kick !== KickIntent.None && b.kickCooldown[id]! <= 0) {
    // ── THE STRIKE BUFFER (§4.2; AI_STRIKE_BUFFER_TICKS) ────────────────────
    // Emit the verb only on a tick the sim will actually honour it. A decided
    // strike is otherwise thrown away — and, worse, CHARGED FOR: the cooldown
    // below used to be spent on the intent, so a carrier whose ball had rolled
    // 0.85 m off the boot (which is most of every knock cycle) paid 18 ticks of
    // silence for a pass that never happened, and decideOnBall's canKick gate
    // then dropped him into the shield branch. MEASURED before this change:
    // 90 % of kick-intent ticks produced no strike, 59 % of strike decisions
    // never reached the input at all, and the delay from "he could legally play
    // it" to "he did" ran at a median of 18 ticks / p90 52.
    //
    // Holding the latch instead is exactly what the client does for a human
    // strike (7dfce32). Nothing else is needed to bound it: Layer 2 clears and
    // re-stamps the latch every 10 Hz decision, and the stamp expires here.
    if (!strikeWouldLand(b, id)) {
      // Hold the intent and try again next tick. Expire it once the read of the
      // pitch behind it is too old to trust — WITHOUT charging the cooldown,
      // because nothing was struck.
      if (b.world.tick - b.decKickTick[id]! >= AI_STRIKE_BUFFER_TICKS) {
        b.decKick[id] = KickIntent.None;
      }
    } else {
      if (kick === KickIntent.Shoot) {
        flags |= InputFlags.Shoot;
        cmd.shootCharge = b.decCharge[id]!;
      } else {
        flags |= InputFlags.Pass;
        if (kick === KickIntent.ThroughBall) flags |= InputFlags.ThroughBall;
        cmd.passCharge = b.decCharge[id]!;
        // §3 - PUBLISH WHO IT IS FOR, here and nowhere else: this is the tick the
        // ball actually leaves the boot, so it is the only tick on which "a pass
        // to T is in flight" is true. Publishing at decision time instead would
        // arm a receiver for a strike the buffer may still be holding.
        const target = b.decPassTarget[id]!;
        if (target >= 0) {
          b.passIntentTarget = target;
          b.passIntentFrom = id;
          b.passIntentTick = b.world.tick;
        }
      }
      // Consume the latch. Without this the same decision would strike the ball
      // on every tick of its 10Hz window; resolveKicks() would only honour the
      // first (it refuses a second touch on one tick), but the other five would
      // sit in the flags and re-fire the instant the ball came loose again.
      //
      // The cooldown is charged HERE, on the tick the verb actually goes out at a
      // ball the striker can reach — i.e. on contact, not on hope. That is what
      // makes AI_KICK_COOLDOWN_TICKS mean "you have just kicked it" instead of
      // "you have just thought about kicking it".
      b.decKick[id] = KickIntent.None;
      b.kickCooldown[id] = AI_KICK_COOLDOWN_TICKS;
    }
  }

  const action = b.decAction[id]!;
  if (action !== ActionIntent.None && b.tackleCooldown[id]! <= 0) {
    flags |= action === ActionIntent.SlideTackle ? InputFlags.SlideTackle : InputFlags.StandingTackle;
    b.decAction[id] = ActionIntent.None;
    b.tackleCooldown[id] = AI_TACKLE_COOLDOWN_TICKS;
  }

  cmd.flags = flags;
}
